// src/data/rareCancersData.js
// Authoritative clinical and SEO dataset for all 39 rare and ultra-rare cancers.
// Generated from backend/rare_cancers/clinical_details.py and seed_data.py.

export const RARE_CANCERS_DATA = [
  {
    "slug": "diffuse-intrinsic-pontine-glioma-dipg",
    "aliases": [
      "dipg",
      "diffuse-midline-glioma",
      "diffuse-intrinsic-pontine-glioma"
    ],
    "path": "/rare-cancers/diffuse-intrinsic-pontine-glioma-dipg",
    "name": "Diffuse Intrinsic Pontine Glioma (DIPG)",
    "shortName": "Diffuse Intrinsic Pontine Glioma",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Pediatric Brain Tumor",
    "description": "A highly aggressive tumour that grows within the pons, the part of the brainstem controlling breathing, swallowing, eye movement and balance. Because it infiltrates rather than forms a removable lump, surgery is not an option. Under the current WHO classification it is grouped as diffuse midline glioma, H3 K27-altered, CNS WHO grade 4.",
    "symptoms": [
      "Double vision or an eye that will not turn outward",
      "Facial droop or asymmetric smile",
      "Unsteady walking, frequent falls",
      "Difficulty swallowing or a change in speech",
      "Morning headache with vomiting (raised pressure)"
    ],
    "treatmentOptions": [
      "Focal radiotherapy (54 Gy) - the only treatment with proven benefit",
      "Re-irradiation at progression in selected children",
      "Clinical trials (ONC201/dordaviprone, CAR-T, convection-enhanced delivery)",
      "Corticosteroids for symptom control",
      "Early integrated palliative and family support"
    ],
    "prognosis": "Poor. Median survival is approximately 9-12 months from diagnosis, and long-term survival beyond two years is uncommon. Radiotherapy usually produces several months of meaningful symptom improvement.",
    "researchStatus": "ONC201 (dordaviprone) in H3 K27M-altered glioma, GD2-directed CAR-T cell therapy, and convection-enhanced delivery to bypass the blood-brain barrier are the most active lines of investigation.",
    "molecularMarkers": [
      "H3F3A K27M",
      "HIST1H3B K27M",
      "ACVR1",
      "TP53",
      "PPM1D"
    ],
    "whoItAffects": "Children, peak age 5-9 years; no strong sex predilection",
    "incidence": "Approximately 2-3 per million children per year",
    "redFlags": [
      "Rapidly progressive cranial nerve palsy in a child",
      "New swallowing difficulty with aspiration"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "RED_FLAGS_ONLY",
    "specialists": [
      {
        "name": "Dr. Mark M. Souweidane",
        "title": "Pediatric Neurosurgeon, Vice Chair of Neurological Surgery",
        "institution": "Weill Cornell Medicine / NewYork-Presbyterian Hospital",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 30,
        "specialties": [
          "Pediatric brain tumors",
          "Brainstem tumors",
          "DIPG"
        ]
      },
      {
        "name": "Dr. Enrica Tan",
        "title": "Senior Consultant, Pediatric Oncologist",
        "institution": "KK Women\u2019s and Children\u2019s Hospital (KKH)",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Pediatric neuro-oncology",
          "Brain tumors",
          "DIPG"
        ]
      },
      {
        "name": "Dr. Jacques Grill",
        "title": "Head, Pediatric Brain Tumor Program",
        "institution": "Gustave Roussy Cancer Campus",
        "city": "Paris",
        "region": "Europe",
        "country": "France",
        "experienceYears": 20,
        "specialties": [
          "Pediatric brain tumors",
          "DIPG",
          "Clinical trials"
        ]
      },
      {
        "name": "Dr. Rakesh Jalali",
        "title": "Radiation Oncologist, Medical Director",
        "institution": "Apollo Proton Cancer Centre",
        "city": "Chennai",
        "region": "India",
        "country": "India",
        "experienceYears": 28,
        "specialties": [
          "Pediatric brain tumors",
          "Proton therapy",
          "DIPG"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Diffuse Intrinsic Pontine Glioma (DIPG) and how is it diagnosed?",
        "answer": "A highly aggressive tumour that grows within the pons, the part of the brainstem controlling breathing, swallowing, eye movement and balance. Because it infiltrates rather than forms a removable lump, surgery is not an option. Under the current WHO classification it is grouped as diffuse midline glioma, H3 K27-altered, CNS WHO grade 4. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Diffuse Intrinsic Pontine Glioma (DIPG)?",
        "answer": "Standard first-line protocols include: Focal radiotherapy (54 Gy) - the only treatment with proven benefit, Re-irradiation at progression in selected children, Clinical trials (ONC201/dordaviprone, CAR-T, convection-enhanced delivery). Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Diffuse Intrinsic Pontine Glioma (DIPG)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: H3F3A K27M, HIST1H3B K27M, ACVR1, TP53, PPM1D. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Rapidly progressive cranial nerve palsy in a child; New swallowing difficulty with aspiration. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "ONC201 (dordaviprone) in H3 K27M-altered glioma, GD2-directed CAR-T cell therapy, and convection-enhanced delivery to bypass the blood-brain barrier are the most active lines of investigation. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Diffuse Intrinsic Pontine Glioma (DIPG) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Diffuse Intrinsic Pontine Glioma (DIPG) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Diffuse Intrinsic Pontine Glioma (DIPG). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/diffuse-intrinsic-pontine-glioma-dipg",
      "keywords": [
        "Diffuse Intrinsic Pontine Glioma (DIPG) treatment",
        "Diffuse Intrinsic Pontine Glioma (DIPG) specialists",
        "Diffuse Intrinsic Pontine Glioma (DIPG) clinical trials",
        "Diffuse Intrinsic Pontine Glioma (DIPG) second opinion",
        "Diffuse Intrinsic Pontine Glioma (DIPG) prognosis",
        "Diffuse Intrinsic Pontine Glioma (DIPG) NCI cancer centers",
        "rare cancer care Diffuse Intrinsic Pontine Glioma"
      ]
    }
  },
  {
    "slug": "atypical-teratoid-rhabdoid-tumor-atrt",
    "aliases": [
      "atrt",
      "atypical-teratoid-rhabdoid-tumor"
    ],
    "path": "/rare-cancers/atypical-teratoid-rhabdoid-tumor-atrt",
    "name": "Atypical Teratoid Rhabdoid Tumor (ATRT)",
    "shortName": "Atypical Teratoid Rhabdoid Tumor",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Pediatric Brain Tumor",
    "description": "An aggressive embryonal tumour of the central nervous system defined by loss of the SMARCB1 (INI1) tumour suppressor. It occurs most often in children under three years, which constrains how much radiotherapy can safely be given.",
    "symptoms": [
      "Vomiting and irritability, often worse in the morning",
      "Enlarging head circumference in infants",
      "Lethargy or loss of developmental milestones",
      "Head tilt, unsteadiness, or cranial nerve deficits",
      "Seizures"
    ],
    "treatmentOptions": [
      "Maximal safe surgical resection",
      "Intensive multi-agent chemotherapy (e.g. ACNS0333-style regimens)",
      "High-dose chemotherapy with autologous stem cell rescue",
      "Focal or craniospinal radiotherapy, age-adapted",
      "Intrathecal chemotherapy in selected protocols"
    ],
    "prognosis": "Historically poor, but materially improved by intensive multimodal therapy at experienced centres. Two-year survival of roughly 40-50% is now reported in trial cohorts; gross total resection, older age at diagnosis and absence of metastasis all predict better outcome.",
    "researchStatus": "EZH2 inhibitors (tazemetostat), CDK4/6 inhibition and immune checkpoint blockade are under study, exploiting the SWI/SNF-deficient state.",
    "molecularMarkers": [
      "SMARCB1 (INI1) loss",
      "SMARCA4 (BRG1) loss - rare variant"
    ],
    "whoItAffects": "Infants and children, usually under 3 years",
    "incidence": "Approximately 1-3 per million children under 3 per year",
    "redFlags": [
      "Rapidly enlarging head in an infant",
      "Acute hydrocephalus"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "RED_FLAGS_ONLY",
    "specialists": [
      {
        "name": "Dr. Amar Gajjar",
        "title": "Pediatric Neuro-Oncologist, Chair of Oncology",
        "institution": "St. Jude Children\u2019s Research Hospital",
        "city": "Memphis",
        "region": "USA",
        "country": "USA",
        "experienceYears": 35,
        "specialties": [
          "Pediatric brain tumors",
          "ATRT",
          "Clinical research"
        ]
      },
      {
        "name": "Dr. Soh Shui Yen",
        "title": "Senior Consultant, Pediatric Hematology/Oncology",
        "institution": "KK Women\u2019s and Children\u2019s Hospital (KKH)",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Pediatric CNS tumors",
          "ATRT",
          "Leukemias"
        ]
      },
      {
        "name": "Prof. Darren Hargrave",
        "title": "Consultant Pediatric Neuro-Oncologist",
        "institution": "Great Ormond Street Hospital",
        "city": "London",
        "region": "Europe",
        "country": "United Kingdom",
        "experienceYears": 20,
        "specialties": [
          "Pediatric brain tumors",
          "ATRT",
          "Early phase trials"
        ]
      },
      {
        "name": "Dr. Girish Chinnaswamy",
        "title": "Professor & Head, Pediatric Oncology",
        "institution": "Tata Memorial Centre",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Pediatric oncology",
          "Neuro-oncology",
          "ATRT"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Atypical Teratoid Rhabdoid Tumor (ATRT) and how is it diagnosed?",
        "answer": "An aggressive embryonal tumour of the central nervous system defined by loss of the SMARCB1 (INI1) tumour suppressor. It occurs most often in children under three years, which constrains how much radiotherapy can safely be given. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Atypical Teratoid Rhabdoid Tumor (ATRT)?",
        "answer": "Standard first-line protocols include: Maximal safe surgical resection, Intensive multi-agent chemotherapy (e.g. ACNS0333-style regimens), High-dose chemotherapy with autologous stem cell rescue. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Atypical Teratoid Rhabdoid Tumor (ATRT)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: SMARCB1 (INI1) loss, SMARCA4 (BRG1) loss - rare variant. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Rapidly enlarging head in an infant; Acute hydrocephalus. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "EZH2 inhibitors (tazemetostat), CDK4/6 inhibition and immune checkpoint blockade are under study, exploiting the SWI/SNF-deficient state. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Atypical Teratoid Rhabdoid Tumor (ATRT) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Atypical Teratoid Rhabdoid Tumor (ATRT) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Atypical Teratoid Rhabdoid Tumor (ATRT). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/atypical-teratoid-rhabdoid-tumor-atrt",
      "keywords": [
        "Atypical Teratoid Rhabdoid Tumor (ATRT) treatment",
        "Atypical Teratoid Rhabdoid Tumor (ATRT) specialists",
        "Atypical Teratoid Rhabdoid Tumor (ATRT) clinical trials",
        "Atypical Teratoid Rhabdoid Tumor (ATRT) second opinion",
        "Atypical Teratoid Rhabdoid Tumor (ATRT) prognosis",
        "Atypical Teratoid Rhabdoid Tumor (ATRT) NCI cancer centers",
        "rare cancer care Atypical Teratoid Rhabdoid Tumor"
      ]
    }
  },
  {
    "slug": "embryonal-tumor-with-multilayered-rosettes-etmr",
    "aliases": [
      "etmr",
      "embryonal-tumor-with-multilayered-rosettes"
    ],
    "path": "/rare-cancers/embryonal-tumor-with-multilayered-rosettes-etmr",
    "name": "Embryonal Tumor with Multilayered Rosettes (ETMR)",
    "shortName": "Embryonal Tumor with Multilayered Rosettes",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Pediatric Brain Tumor",
    "description": "An ultra-rare, highly malignant embryonal brain tumour of very young children, defined by amplification of the C19MC microRNA cluster at chromosome 19q13.42 and strong LIN28A expression. It arises most often in the cerebral hemispheres but also in the brainstem and cerebellum.",
    "symptoms": [
      "Seizures",
      "Rapidly increasing head circumference",
      "Persistent vomiting and irritability",
      "Focal weakness or asymmetric movement",
      "Regression of milestones"
    ],
    "treatmentOptions": [
      "Maximal safe resection - extent of resection is a strong prognostic factor",
      "Intensive induction chemotherapy",
      "High-dose chemotherapy with stem cell rescue",
      "Radiotherapy in children old enough to receive it safely",
      "Clinical trial enrolment wherever available"
    ],
    "prognosis": "Generally poor, with median survival historically under 12 months. Outcomes are meaningfully better in children who achieve gross total resection and are treated on intensive protocols at specialist centres.",
    "researchStatus": "LIN28A/let-7 axis targeting, ALK and IGF1R inhibition, and refined risk-adapted protocols are the principal research directions.",
    "molecularMarkers": [
      "C19MC amplification (19q13.42)",
      "LIN28A overexpression",
      "DICER1 - variant form"
    ],
    "whoItAffects": "Children, usually under 4 years; slight female predominance",
    "incidence": "Fewer than 1 per million children per year",
    "redFlags": [],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Maryam Fouladi",
        "title": "Co-Director, Pediatric Neuro-Oncology Program",
        "institution": "Nationwide Children\u2019s Hospital",
        "city": "Columbus",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Pediatric brain tumors",
          "ETMR",
          "Clinical trials"
        ]
      },
      {
        "name": "Dr. Chan Mei Yoke",
        "title": "Senior Consultant, Pediatric Oncology",
        "institution": "KK Women\u2019s and Children\u2019s Hospital (KKH)",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Pediatric oncology",
          "Rare brain tumors",
          "ETMR"
        ]
      },
      {
        "name": "Prof. Stefan Pfister",
        "title": "Director, Hopp Children\u2019s Cancer Center",
        "institution": "Hopp Children\u2019s Cancer Center / Heidelberg University",
        "city": "Heidelberg",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 15,
        "specialties": [
          "Pediatric neuro-oncology",
          "Genomics",
          "ETMR"
        ]
      },
      {
        "name": "Dr. Sameer Bakshi",
        "title": "Professor of Medical Oncology",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Pediatric and adolescent oncology",
          "Rare CNS tumors",
          "ETMR"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Embryonal Tumor with Multilayered Rosettes (ETMR) and how is it diagnosed?",
        "answer": "An ultra-rare, highly malignant embryonal brain tumour of very young children, defined by amplification of the C19MC microRNA cluster at chromosome 19q13.42 and strong LIN28A expression. It arises most often in the cerebral hemispheres but also in the brainstem and cerebellum. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Embryonal Tumor with Multilayered Rosettes (ETMR)?",
        "answer": "Standard first-line protocols include: Maximal safe resection - extent of resection is a strong prognostic factor, Intensive induction chemotherapy, High-dose chemotherapy with stem cell rescue. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Embryonal Tumor with Multilayered Rosettes (ETMR)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: C19MC amplification (19q13.42), LIN28A overexpression, DICER1 - variant form. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "LIN28A/let-7 axis targeting, ALK and IGF1R inhibition, and refined risk-adapted protocols are the principal research directions. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Embryonal Tumor with Multilayered Rosettes (ETMR) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Embryonal Tumor with Multilayered Rosettes (ETMR) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Embryonal Tumor with Multilayered Rosettes (ETMR). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/embryonal-tumor-with-multilayered-rosettes-etmr",
      "keywords": [
        "Embryonal Tumor with Multilayered Rosettes (ETMR) treatment",
        "Embryonal Tumor with Multilayered Rosettes (ETMR) specialists",
        "Embryonal Tumor with Multilayered Rosettes (ETMR) clinical trials",
        "Embryonal Tumor with Multilayered Rosettes (ETMR) second opinion",
        "Embryonal Tumor with Multilayered Rosettes (ETMR) prognosis",
        "Embryonal Tumor with Multilayered Rosettes (ETMR) NCI cancer centers",
        "rare cancer care Embryonal Tumor with Multilayered Rosettes"
      ]
    }
  },
  {
    "slug": "midline-nut-carcinoma",
    "aliases": [
      "nut-carcinoma",
      "nut-midline-carcinoma"
    ],
    "path": "/rare-cancers/midline-nut-carcinoma",
    "name": "Midline NUT Carcinoma",
    "shortName": "Midline NUT Carcinoma",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Rare Carcinoma",
    "description": "A very aggressive squamous carcinoma defined by rearrangement of the NUTM1 gene, most often fused to BRD4. It arises along the midline - sinonasal tract, mediastinum, lung - and is frequently mistaken for poorly differentiated squamous cell carcinoma unless NUT immunohistochemistry is specifically requested.",
    "symptoms": [
      "Rapidly enlarging neck, sinus or chest mass",
      "Nasal obstruction or epistaxis",
      "Cough, chest pain, breathlessness",
      "Superior vena cava obstruction (facial swelling, distended neck veins)",
      "Bone pain from early metastasis"
    ],
    "treatmentOptions": [
      "Surgery where the tumour is resectable",
      "Intensive platinum-based chemotherapy",
      "Radiotherapy for local control",
      "BET inhibitor or HDAC inhibitor clinical trials",
      "Early palliative care given the pace of the disease"
    ],
    "prognosis": "Poor. Median overall survival is around 6-7 months, though a minority of patients with resectable non-thoracic disease treated aggressively do considerably better.",
    "researchStatus": "BET bromodomain inhibitors are the rational targeted approach and remain in trials; combinations with HDAC inhibition and CDK9 blockade are being explored.",
    "molecularMarkers": [
      "NUTM1 rearrangement",
      "BRD4-NUTM1 fusion",
      "BRD3-NUTM1 fusion",
      "NSD3-NUTM1 fusion"
    ],
    "whoItAffects": "Any age; median in the 20s-30s, but reported from infancy to old age",
    "incidence": "Genuinely unknown - substantially under-diagnosed without NUT IHC",
    "redFlags": [
      "Undifferentiated midline carcinoma in a young patient - request NUT IHC",
      "Superior vena cava obstruction"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "RED_FLAGS_ONLY",
    "specialists": [
      {
        "name": "Dr. Jia Luo",
        "title": "Medical Oncologist, Thoracic Oncology",
        "institution": "Dana-Farber Cancer Institute",
        "city": "Boston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 15,
        "specialties": [
          "Thoracic Oncology",
          "NUT Carcinoma",
          "Rare Carcinomas"
        ]
      },
      {
        "name": "Dr. Toh Chee Keong",
        "title": "Senior Consultant Medical Oncologist",
        "institution": "NCCS/OncoCare",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Thoracic Oncology",
          "Head & Neck Cancers",
          "NUT Carcinoma"
        ]
      },
      {
        "name": "Prof. Lisa Licitra",
        "title": "Head of Head & Neck Oncology",
        "institution": "Istituto Nazionale Tumori",
        "city": "Milan",
        "region": "Europe",
        "country": "Italy",
        "experienceYears": 30,
        "specialties": [
          "Head & Neck Oncology",
          "Rare Head-Neck Cancers",
          "NUT Carcinoma"
        ]
      },
      {
        "name": "Dr. Anil D'Cruz",
        "title": "Surgical Oncologist, Ex-Director",
        "institution": "Tata Memorial Hospital",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 33,
        "specialties": [
          "Head-Neck Cancer",
          "Surgical Oncology",
          "NUT Carcinoma"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Midline NUT Carcinoma and how is it diagnosed?",
        "answer": "A very aggressive squamous carcinoma defined by rearrangement of the NUTM1 gene, most often fused to BRD4. It arises along the midline - sinonasal tract, mediastinum, lung - and is frequently mistaken for poorly differentiated squamous cell carcinoma unless NUT immunohistochemistry is specifically requested. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Midline NUT Carcinoma?",
        "answer": "Standard first-line protocols include: Surgery where the tumour is resectable, Intensive platinum-based chemotherapy, Radiotherapy for local control. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Midline NUT Carcinoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: NUTM1 rearrangement, BRD4-NUTM1 fusion, BRD3-NUTM1 fusion, NSD3-NUTM1 fusion. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Undifferentiated midline carcinoma in a young patient - request NUT IHC; Superior vena cava obstruction. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "BET bromodomain inhibitors are the rational targeted approach and remain in trials; combinations with HDAC inhibition and CDK9 blockade are being explored. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Midline NUT Carcinoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Midline NUT Carcinoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Midline NUT Carcinoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/midline-nut-carcinoma",
      "keywords": [
        "Midline NUT Carcinoma treatment",
        "Midline NUT Carcinoma specialists",
        "Midline NUT Carcinoma clinical trials",
        "Midline NUT Carcinoma second opinion",
        "Midline NUT Carcinoma prognosis",
        "Midline NUT Carcinoma NCI cancer centers",
        "rare cancer care Midline NUT Carcinoma"
      ]
    }
  },
  {
    "slug": "small-cell-carcinoma-of-the-ovary-hypercalcemic-type-sccoht",
    "aliases": [
      "sccoht",
      "small-cell-ovarian-hypercalcemic"
    ],
    "path": "/rare-cancers/small-cell-carcinoma-of-the-ovary-hypercalcemic-type-sccoht",
    "name": "Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT)",
    "shortName": "Small Cell Carcinoma of the Ovary, Hypercalcemic Type",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Ovarian Cancer",
    "description": "A rare and aggressive ovarian tumour of young women, driven by inactivation of SMARCA4 (BRG1). Around two-thirds of patients have raised blood calcium at presentation, which is what gives the disease its name. Despite the name it is not related to small cell lung cancer.",
    "symptoms": [
      "Abdominal or pelvic pain and a palpable mass",
      "Abdominal distension",
      "Symptoms of hypercalcaemia - thirst, confusion, constipation, nausea",
      "Irregular menstrual bleeding",
      "Unintended weight loss"
    ],
    "treatmentOptions": [
      "Surgical staging and cytoreduction",
      "Intensive multi-agent chemotherapy (platinum-based)",
      "High-dose chemotherapy with autologous stem cell rescue in selected patients",
      "Radiotherapy to the pelvis in some protocols",
      "Correction of hypercalcaemia as an urgent supportive measure"
    ],
    "prognosis": "Poor overall, with the majority of patients relapsing, but long-term remission is reported in early-stage disease treated with aggressive multimodal therapy including stem cell rescue.",
    "researchStatus": "EZH2 inhibition (tazemetostat) exploits the SMARCA4-deficient state and is the leading targeted strategy; immune checkpoint blockade has shown activity in case series and is under formal study.",
    "molecularMarkers": [
      "SMARCA4 (BRG1) loss-of-function",
      "Germline SMARCA4 in a subset"
    ],
    "whoItAffects": "Young women and adolescent girls; median age around 24 years",
    "incidence": "Fewer than 1 per million women per year",
    "redFlags": [
      "Ovarian mass with hypercalcaemia in a woman under 40 - test SMARCA4"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Douglas A. Levine",
        "title": "Gynecologic Oncologist",
        "institution": "NYU Langone (formerly MSKCC)",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 25,
        "specialties": [
          "Gynecologic Oncology",
          "SCCOHT",
          "Ovarian Cancers"
        ]
      },
      {
        "name": "Dr. Jeffrey Low",
        "title": "Senior Consultant Gynecologic Oncologist",
        "institution": "National University Cancer Institute Singapore (NUH)",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Gynecologic Oncology",
          "Rare Ovarian Cancers",
          "SCCOHT"
        ]
      },
      {
        "name": "Prof. Susana Banerjee",
        "title": "Medical Oncologist",
        "institution": "Royal Marsden Hospital",
        "city": "London",
        "region": "Europe",
        "country": "United Kingdom",
        "experienceYears": 24,
        "specialties": [
          "Gynecologic Cancers",
          "Rare Ovarian Tumors",
          "SCCOHT"
        ]
      },
      {
        "name": "Dr. Amita Maheshwari",
        "title": "Professor & Head, Gyne-Oncology",
        "institution": "Tata Memorial Hospital",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Gynecologic Oncology",
          "Rare Ovarian Cancers",
          "SCCOHT"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) and how is it diagnosed?",
        "answer": "A rare and aggressive ovarian tumour of young women, driven by inactivation of SMARCA4 (BRG1). Around two-thirds of patients have raised blood calcium at presentation, which is what gives the disease its name. Despite the name it is not related to small cell lung cancer. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT)?",
        "answer": "Standard first-line protocols include: Surgical staging and cytoreduction, Intensive multi-agent chemotherapy (platinum-based), High-dose chemotherapy with autologous stem cell rescue in selected patients. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: SMARCA4 (BRG1) loss-of-function, Germline SMARCA4 in a subset. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Ovarian mass with hypercalcaemia in a woman under 40 - test SMARCA4. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "EZH2 inhibition (tazemetostat) exploits the SMARCA4-deficient state and is the leading targeted strategy; immune checkpoint blockade has shown activity in case series and is under formal study. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/small-cell-carcinoma-of-the-ovary-hypercalcemic-type-sccoht",
      "keywords": [
        "Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) treatment",
        "Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) specialists",
        "Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) clinical trials",
        "Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) second opinion",
        "Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) prognosis",
        "Small Cell Carcinoma of the Ovary, Hypercalcemic Type (SCCOHT) NCI cancer centers",
        "rare cancer care Small Cell Carcinoma of the Ovary, Hypercalcemic Type"
      ]
    }
  },
  {
    "slug": "anaplastic-thyroid-carcinoma-atc",
    "aliases": [
      "atc",
      "anaplastic-thyroid-cancer"
    ],
    "path": "/rare-cancers/anaplastic-thyroid-carcinoma-atc",
    "name": "Anaplastic Thyroid Carcinoma (ATC)",
    "shortName": "Anaplastic Thyroid Carcinoma",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Thyroid Cancer",
    "description": "The most aggressive thyroid malignancy, in which thyroid cells lose all differentiated features. It is classified as stage IV by definition at diagnosis. Airway compromise is the immediate threat and is what dictates the first hours of management.",
    "symptoms": [
      "Rapidly enlarging, hard neck mass over weeks",
      "Hoarseness or voice change",
      "Difficulty swallowing",
      "Noisy or obstructed breathing (stridor)",
      "Neck pain and fixed, non-mobile swelling"
    ],
    "treatmentOptions": [
      "Urgent airway assessment and securing the airway where needed",
      "BRAF/MEK inhibition (dabrafenib + trametinib) if BRAF V600E-mutated",
      "Surgery where an R0/R1 resection is achievable",
      "Concurrent chemoradiotherapy",
      "Immunotherapy, including pembrolizumab in combination protocols",
      "Neoadjuvant targeted therapy to convert unresectable to resectable disease"
    ],
    "prognosis": "Historically 3-6 months median survival. Molecularly guided therapy has changed this materially for the BRAF V600E-mutated subset, where neoadjuvant dabrafenib/trametinib followed by surgery has produced one-year survival substantially above historical rates.",
    "researchStatus": "Neoadjuvant targeted therapy followed by resection, RET and NTRK inhibitors in fusion-positive disease, and checkpoint inhibitor combinations are all in active study.",
    "molecularMarkers": [
      "BRAF V600E",
      "TERT promoter",
      "TP53",
      "RAS",
      "PIK3CA",
      "NTRK/RET fusions"
    ],
    "whoItAffects": "Older adults, typically over 60; slight female predominance",
    "incidence": "Approximately 1-2 per million per year",
    "redFlags": [
      "Stridor or any airway compromise - emergency",
      "Neck mass doubling in size within weeks - same-week molecular testing"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "RED_FLAGS_ONLY",
    "specialists": [
      {
        "name": "Dr. Maria E. Cabanillas",
        "title": "Oncologic Endocrinologist",
        "institution": "MD Anderson Cancer Center",
        "city": "Houston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Endocrine Oncology",
          "Thyroid Cancer",
          "ATC"
        ]
      },
      {
        "name": "Dr. Samantha Yang Peiling",
        "title": "Endocrinologist",
        "institution": "NCIS/NUH Singapore",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Thyroid Cancer",
          "Endocrinology",
          "ATC"
        ]
      },
      {
        "name": "Prof. Martin Schlumberger",
        "title": "Oncologist",
        "institution": "Gustave Roussy",
        "city": "Paris",
        "region": "Europe",
        "country": "France",
        "experienceYears": 40,
        "specialties": [
          "Thyroid Cancers",
          "Endocrine Oncology",
          "ATC"
        ]
      },
      {
        "name": "Dr. Sanu P. Moideen",
        "title": "Head & Neck Oncosurgeon (ENT)",
        "institution": "Private Practice",
        "city": "Kerala",
        "region": "India",
        "country": "India",
        "experienceYears": 25,
        "specialties": [
          "Head & Neck Surgery",
          "Thyroid Cancer",
          "ATC"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Anaplastic Thyroid Carcinoma (ATC) and how is it diagnosed?",
        "answer": "The most aggressive thyroid malignancy, in which thyroid cells lose all differentiated features. It is classified as stage IV by definition at diagnosis. Airway compromise is the immediate threat and is what dictates the first hours of management. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Anaplastic Thyroid Carcinoma (ATC)?",
        "answer": "Standard first-line protocols include: Urgent airway assessment and securing the airway where needed, BRAF/MEK inhibition (dabrafenib + trametinib) if BRAF V600E-mutated, Surgery where an R0/R1 resection is achievable. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Anaplastic Thyroid Carcinoma (ATC)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: BRAF V600E, TERT promoter, TP53, RAS, PIK3CA, NTRK/RET fusions. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Stridor or any airway compromise - emergency; Neck mass doubling in size within weeks - same-week molecular testing. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Neoadjuvant targeted therapy followed by resection, RET and NTRK inhibitors in fusion-positive disease, and checkpoint inhibitor combinations are all in active study. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Anaplastic Thyroid Carcinoma (ATC) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Anaplastic Thyroid Carcinoma (ATC) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Anaplastic Thyroid Carcinoma (ATC). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/anaplastic-thyroid-carcinoma-atc",
      "keywords": [
        "Anaplastic Thyroid Carcinoma (ATC) treatment",
        "Anaplastic Thyroid Carcinoma (ATC) specialists",
        "Anaplastic Thyroid Carcinoma (ATC) clinical trials",
        "Anaplastic Thyroid Carcinoma (ATC) second opinion",
        "Anaplastic Thyroid Carcinoma (ATC) prognosis",
        "Anaplastic Thyroid Carcinoma (ATC) NCI cancer centers",
        "rare cancer care Anaplastic Thyroid Carcinoma"
      ]
    }
  },
  {
    "slug": "desmoplastic-small-round-cell-tumor-dsrct",
    "aliases": [
      "dsrct",
      "desmoplastic-small-round-cell-tumor"
    ],
    "path": "/rare-cancers/desmoplastic-small-round-cell-tumor-dsrct",
    "name": "Desmoplastic Small Round Cell Tumor (DSRCT)",
    "shortName": "Desmoplastic Small Round Cell Tumor",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Sarcoma",
    "description": "A rare, aggressive soft-tissue sarcoma driven by the EWSR1-WT1 fusion gene. It typically fills the abdominal and pelvic cavity with multiple tumour deposits rather than a single mass, and predominantly affects adolescent and young adult males.",
    "symptoms": [
      "Abdominal pain and progressive distension",
      "A palpable abdominal mass",
      "Weight loss and fatigue",
      "Constipation or urinary obstruction from pelvic deposits",
      "Ascites"
    ],
    "treatmentOptions": [
      "Multi-agent chemotherapy (P6-type regimens with alkylating agents)",
      "Aggressive cytoreductive surgery aiming for near-complete debulking",
      "Hyperthermic intraperitoneal chemotherapy (HIPEC) in selected patients",
      "Whole-abdominopelvic radiotherapy",
      "Clinical trial enrolment"
    ],
    "prognosis": "Poor, with five-year survival historically around 15-20%. Patients who achieve greater than 90% cytoreduction alongside systemic therapy have meaningfully longer survival.",
    "researchStatus": "Targeting the EWSR1-WT1 fusion directly, IGF1R inhibition, WT1-directed immunotherapy and vaccine approaches are all under investigation.",
    "molecularMarkers": [
      "EWSR1-WT1 fusion, t(11;22)(p13;q12)"
    ],
    "whoItAffects": "Adolescents and young adults; strong male predominance (about 4:1)",
    "incidence": "Fewer than 1 per million per year",
    "redFlags": [],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Andrea Hayes-Jordan",
        "title": "Pediatric Surgical Oncologist",
        "institution": "UNC (formerly MD Anderson)",
        "city": "Chapel Hill",
        "region": "USA",
        "country": "USA",
        "experienceYears": 25,
        "specialties": [
          "Pediatric Surgical Oncology",
          "DSRCT",
          "HIPEC"
        ]
      },
      {
        "name": "Dr. Richard Quek",
        "title": "Senior Consultant Medical Oncologist",
        "institution": "NCCS (formerly)",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 16,
        "specialties": [
          "Sarcoma Oncology",
          "DSRCT",
          "Multimodal Therapy"
        ]
      },
      {
        "name": "Prof. Jeremy Whelan",
        "title": "Medical Oncologist",
        "institution": "UCLH",
        "city": "London",
        "region": "Europe",
        "country": "United Kingdom",
        "experienceYears": 24,
        "specialties": [
          "Sarcoma Oncology",
          "Rare Abdominal Sarcomas",
          "DSRCT"
        ]
      },
      {
        "name": "Dr. Sameer Rastogi",
        "title": "Sarcoma Medical Oncologist",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Sarcoma Oncology",
          "Rare Sarcomas",
          "DSRCT"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Desmoplastic Small Round Cell Tumor (DSRCT) and how is it diagnosed?",
        "answer": "A rare, aggressive soft-tissue sarcoma driven by the EWSR1-WT1 fusion gene. It typically fills the abdominal and pelvic cavity with multiple tumour deposits rather than a single mass, and predominantly affects adolescent and young adult males. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Desmoplastic Small Round Cell Tumor (DSRCT)?",
        "answer": "Standard first-line protocols include: Multi-agent chemotherapy (P6-type regimens with alkylating agents), Aggressive cytoreductive surgery aiming for near-complete debulking, Hyperthermic intraperitoneal chemotherapy (HIPEC) in selected patients. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Desmoplastic Small Round Cell Tumor (DSRCT)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: EWSR1-WT1 fusion, t(11;22)(p13;q12). Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Targeting the EWSR1-WT1 fusion directly, IGF1R inhibition, WT1-directed immunotherapy and vaccine approaches are all under investigation. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Desmoplastic Small Round Cell Tumor (DSRCT) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Desmoplastic Small Round Cell Tumor (DSRCT) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Desmoplastic Small Round Cell Tumor (DSRCT). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/desmoplastic-small-round-cell-tumor-dsrct",
      "keywords": [
        "Desmoplastic Small Round Cell Tumor (DSRCT) treatment",
        "Desmoplastic Small Round Cell Tumor (DSRCT) specialists",
        "Desmoplastic Small Round Cell Tumor (DSRCT) clinical trials",
        "Desmoplastic Small Round Cell Tumor (DSRCT) second opinion",
        "Desmoplastic Small Round Cell Tumor (DSRCT) prognosis",
        "Desmoplastic Small Round Cell Tumor (DSRCT) NCI cancer centers",
        "rare cancer care Desmoplastic Small Round Cell Tumor"
      ]
    }
  },
  {
    "slug": "epithelioid-hemangioendothelioma-ehe",
    "aliases": [
      "ehe",
      "epithelioid-hemangioendothelioma"
    ],
    "path": "/rare-cancers/epithelioid-hemangioendothelioma-ehe",
    "name": "Epithelioid Hemangioendothelioma (EHE)",
    "shortName": "Epithelioid Hemangioendothelioma",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Vascular Tumor",
    "description": "A vascular tumour of intermediate malignancy arising from the cells that line blood vessels. Its behaviour is unusually variable - some patients remain stable without treatment for years, while others have rapidly progressive disease. Liver, lung and bone are the commonest sites.",
    "symptoms": [
      "Often none - frequently found incidentally on imaging",
      "Pain at the affected site, especially bone lesions",
      "Cough, breathlessness or pleuritic pain with lung involvement",
      "Right upper abdominal discomfort with liver involvement",
      "Fatigue, weight loss and fever in aggressive disease"
    ],
    "treatmentOptions": [
      "Active surveillance for stable, asymptomatic disease",
      "Surgical resection of localised lesions",
      "Liver transplantation in selected hepatic-only disease",
      "mTOR inhibition (sirolimus)",
      "Antiangiogenic therapy or interferon in progressive disease"
    ],
    "prognosis": "Highly variable. Many patients live for many years with stable disease. Serosal effusions, systemic symptoms and high mitotic count identify the aggressive subgroup with markedly shorter survival.",
    "researchStatus": "The EHE Rare Cancer Charity and international consortia are running trials of TAZ/YAP-TEAD inhibitors, which target the fusion oncoprotein directly; trametinib and sirolimus have prospective data.",
    "molecularMarkers": [
      "WWTR1-CAMTA1 fusion (about 90%)",
      "YAP1-TFE3 fusion (variant)"
    ],
    "whoItAffects": "Adults across a wide age range; slight female predominance",
    "incidence": "Approximately 1 per million per year",
    "redFlags": [],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Vinod Ravi",
        "title": "Sarcoma Medical Oncologist",
        "institution": "MD Anderson",
        "city": "Houston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Sarcoma Oncology",
          "Vascular Sarcomas",
          "EHE"
        ]
      },
      {
        "name": "Dr. Richard Quek",
        "title": "Senior Consultant Medical Oncologist",
        "institution": "Sarcoma Consortium",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 16,
        "specialties": [
          "Sarcoma Oncology",
          "EHE",
          "Rare Sarcomas"
        ]
      },
      {
        "name": "Prof. Jean-Yves Blay",
        "title": "Oncologist, Director",
        "institution": "Centre L\u00e9on B\u00e9rard",
        "city": "Lyon",
        "region": "Europe",
        "country": "France",
        "experienceYears": 25,
        "specialties": [
          "Rare Sarcomas",
          "EHE",
          "Angiosarcomas"
        ]
      },
      {
        "name": "Dr. Sameer Rastogi",
        "title": "Sarcoma Medical Oncologist",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Sarcoma Oncology",
          "Rare Vascular Tumors",
          "EHE"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Epithelioid Hemangioendothelioma (EHE) and how is it diagnosed?",
        "answer": "A vascular tumour of intermediate malignancy arising from the cells that line blood vessels. Its behaviour is unusually variable - some patients remain stable without treatment for years, while others have rapidly progressive disease. Liver, lung and bone are the commonest sites. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Epithelioid Hemangioendothelioma (EHE)?",
        "answer": "Standard first-line protocols include: Active surveillance for stable, asymptomatic disease, Surgical resection of localised lesions, Liver transplantation in selected hepatic-only disease. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Epithelioid Hemangioendothelioma (EHE)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: WWTR1-CAMTA1 fusion (about 90%), YAP1-TFE3 fusion (variant). Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "The EHE Rare Cancer Charity and international consortia are running trials of TAZ/YAP-TEAD inhibitors, which target the fusion oncoprotein directly; trametinib and sirolimus have prospective data. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Epithelioid Hemangioendothelioma (EHE) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Epithelioid Hemangioendothelioma (EHE) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Epithelioid Hemangioendothelioma (EHE). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/epithelioid-hemangioendothelioma-ehe",
      "keywords": [
        "Epithelioid Hemangioendothelioma (EHE) treatment",
        "Epithelioid Hemangioendothelioma (EHE) specialists",
        "Epithelioid Hemangioendothelioma (EHE) clinical trials",
        "Epithelioid Hemangioendothelioma (EHE) second opinion",
        "Epithelioid Hemangioendothelioma (EHE) prognosis",
        "Epithelioid Hemangioendothelioma (EHE) NCI cancer centers",
        "rare cancer care Epithelioid Hemangioendothelioma"
      ]
    }
  },
  {
    "slug": "histiocytic-sarcoma",
    "aliases": [
      "histiocytic-sarcoma"
    ],
    "path": "/rare-cancers/histiocytic-sarcoma",
    "name": "Histiocytic Sarcoma",
    "shortName": "Histiocytic Sarcoma",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Sarcoma",
    "description": "A rare malignancy of mature tissue macrophages. It may arise on its own or transform from a prior lymphoid neoplasm such as follicular lymphoma or CLL, in which case the two share a clonal origin. Diagnosis depends on immunohistochemistry (CD163, CD68, lysozyme) to exclude the far commoner lymphomas and melanoma.",
    "symptoms": [
      "Painless enlarging lymph nodes or a soft-tissue mass",
      "Fever, night sweats and weight loss",
      "Skin nodules or plaques",
      "Abdominal pain with bowel or liver involvement",
      "Cytopenias if bone marrow is involved"
    ],
    "treatmentOptions": [
      "Surgical resection for localised disease",
      "Radiotherapy for localised or residual disease",
      "Multi-agent chemotherapy (lymphoma-style regimens) for disseminated disease",
      "MAPK pathway inhibition where BRAF or MAP2K1 alterations are found",
      "Allogeneic transplant considered in fit patients with responsive disease"
    ],
    "prognosis": "Guarded. Localised disease that is completely resected can do well; disseminated disease has historically had a median survival under a year, though targeted therapy is changing this in mutation-positive cases.",
    "researchStatus": "Molecular profiling for MAPK pathway alterations is now standard practice at expert centres, and MEK/BRAF inhibitors are being used with reported durable responses; CSF1R-directed agents are under study.",
    "molecularMarkers": [
      "BRAF V600E",
      "MAP2K1",
      "KRAS/NRAS",
      "CD163+/CD68+/lysozyme+ by IHC"
    ],
    "whoItAffects": "Adults, median age around 50; wide range including children",
    "incidence": "Fewer than 1 per million per year",
    "redFlags": [],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Eli L. Diamond",
        "title": "Neuro-oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 10,
        "specialties": [
          "Histiocytic Disorders",
          "Rare Histiocytic Neoplasms",
          "Histiocytic Sarcoma"
        ]
      },
      {
        "name": "Dr. Kevin Tay",
        "title": "Hematologist-Oncologist",
        "institution": "OncoCare / NCCS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Hematology-Oncology",
          "Lymphomas",
          "Histiocytic Sarcoma"
        ]
      },
      {
        "name": "Dr. Julien Haroche",
        "title": "Internist",
        "institution": "Piti\u00e9-Salp\u00eatri\u00e8re Hospital",
        "city": "Paris",
        "region": "Europe",
        "country": "France",
        "experienceYears": 20,
        "specialties": [
          "Histiocytoses",
          "Erdheim-Chester Disease",
          "Histiocytic Sarcoma"
        ]
      },
      {
        "name": "Dr. Sameer Rastogi",
        "title": "Sarcoma Medical Oncologist",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Rare Cancer Specialist",
          "Histiocytic Sarcoma",
          "Multimodal Treatment"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Histiocytic Sarcoma and how is it diagnosed?",
        "answer": "A rare malignancy of mature tissue macrophages. It may arise on its own or transform from a prior lymphoid neoplasm such as follicular lymphoma or CLL, in which case the two share a clonal origin. Diagnosis depends on immunohistochemistry (CD163, CD68, lysozyme) to exclude the far commoner lymphomas and melanoma. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Histiocytic Sarcoma?",
        "answer": "Standard first-line protocols include: Surgical resection for localised disease, Radiotherapy for localised or residual disease, Multi-agent chemotherapy (lymphoma-style regimens) for disseminated disease. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Histiocytic Sarcoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: BRAF V600E, MAP2K1, KRAS/NRAS, CD163+/CD68+/lysozyme+ by IHC. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Molecular profiling for MAPK pathway alterations is now standard practice at expert centres, and MEK/BRAF inhibitors are being used with reported durable responses; CSF1R-directed agents are under study. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Histiocytic Sarcoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Histiocytic Sarcoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Histiocytic Sarcoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/histiocytic-sarcoma",
      "keywords": [
        "Histiocytic Sarcoma treatment",
        "Histiocytic Sarcoma specialists",
        "Histiocytic Sarcoma clinical trials",
        "Histiocytic Sarcoma second opinion",
        "Histiocytic Sarcoma prognosis",
        "Histiocytic Sarcoma NCI cancer centers",
        "rare cancer care Histiocytic Sarcoma"
      ]
    }
  },
  {
    "slug": "erdheim-chester-disease-ecd",
    "aliases": [
      "ecd",
      "erdheim-chester"
    ],
    "path": "/rare-cancers/erdheim-chester-disease-ecd",
    "name": "Erdheim-Chester Disease (ECD)",
    "shortName": "Erdheim-Chester Disease",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Histiocytic Disorder",
    "description": "A rare histiocytic neoplasm in which foamy, lipid-laden histiocytes infiltrate bone, the tissue around the kidneys, the aorta, the heart, the pituitary and the brain. It was long thought to be an inflammatory condition; the discovery that around half of cases carry BRAF V600E reclassified it as a clonal neoplasm and transformed its treatment.",
    "symptoms": [
      "Bone pain, characteristically in both lower legs",
      "Excessive thirst and urination (diabetes insipidus)",
      "Fatigue, fever and night sweats",
      "Bulging eyes (exophthalmos) or eyelid xanthelasma",
      "Breathlessness, kidney impairment, or neurological problems including ataxia"
    ],
    "treatmentOptions": [
      "Vemurafenib for BRAF V600E-mutated disease (regulatory-approved indication)",
      "MEK inhibition (cobimetinib) for BRAF wild-type or MAP2K1-mutated disease",
      "Interferon-alpha / pegylated interferon as an older first-line option",
      "Anakinra or infliximab in selected patients",
      "Organ-specific supportive care - desmopressin, cardiac and renal management"
    ],
    "prognosis": "Historically poor with cardiac or central nervous system involvement, but substantially improved by MAPK-targeted therapy. Many patients now achieve durable disease control; central nervous system involvement remains the strongest adverse prognostic factor.",
    "researchStatus": "Cobimetinib has demonstrated activity across histiocytic neoplasms regardless of mutation, and pan-histiocytosis basket trials are the main route to new agents.",
    "molecularMarkers": [
      "BRAF V600E (about 50%)",
      "MAP2K1",
      "NRAS",
      "PIK3CA",
      "ALK fusions - rare"
    ],
    "whoItAffects": "Adults, typically 40-70 years; male predominance",
    "incidence": "Fewer than 1 per million; roughly 1,500 cases reported worldwide",
    "redFlags": [
      "Symmetric sclerosis of the long bones on imaging",
      "Perinephric infiltration ('hairy kidney') or circumferential aortic sheathing"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Eli L. Diamond",
        "title": "Neuro-oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 10,
        "specialties": [
          "Erdheim-Chester Disease",
          "Histiocytic Disorders",
          "Clinical Trials"
        ]
      },
      {
        "name": "Dr. Chng Wee Joo",
        "title": "Senior Hematologist",
        "institution": "NUH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 40,
        "specialties": [
          "Hematology",
          "Histiocytic Disorders",
          "ECD"
        ]
      },
      {
        "name": "Dr. Julien Haroche",
        "title": "Internist",
        "institution": "Piti\u00e9-Salp\u00eatri\u00e8re Hospital",
        "city": "Paris",
        "region": "Europe",
        "country": "France",
        "experienceYears": 20,
        "specialties": [
          "Erdheim-Chester Disease",
          "Histiocytic Neoplasms",
          "Targeted Therapies"
        ]
      },
      {
        "name": "Dr. Sameer Rastogi",
        "title": "Sarcoma Medical Oncologist",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Rare Histiocytoses",
          "ECD",
          "Targeted Therapies"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Erdheim-Chester Disease (ECD) and how is it diagnosed?",
        "answer": "A rare histiocytic neoplasm in which foamy, lipid-laden histiocytes infiltrate bone, the tissue around the kidneys, the aorta, the heart, the pituitary and the brain. It was long thought to be an inflammatory condition; the discovery that around half of cases carry BRAF V600E reclassified it as a clonal neoplasm and transformed its treatment. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Erdheim-Chester Disease (ECD)?",
        "answer": "Standard first-line protocols include: Vemurafenib for BRAF V600E-mutated disease (regulatory-approved indication), MEK inhibition (cobimetinib) for BRAF wild-type or MAP2K1-mutated disease, Interferon-alpha / pegylated interferon as an older first-line option. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Erdheim-Chester Disease (ECD)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: BRAF V600E (about 50%), MAP2K1, NRAS, PIK3CA, ALK fusions - rare. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Symmetric sclerosis of the long bones on imaging; Perinephric infiltration ('hairy kidney') or circumferential aortic sheathing. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Cobimetinib has demonstrated activity across histiocytic neoplasms regardless of mutation, and pan-histiocytosis basket trials are the main route to new agents. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Erdheim-Chester Disease (ECD) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Erdheim-Chester Disease (ECD) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Erdheim-Chester Disease (ECD). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/erdheim-chester-disease-ecd",
      "keywords": [
        "Erdheim-Chester Disease (ECD) treatment",
        "Erdheim-Chester Disease (ECD) specialists",
        "Erdheim-Chester Disease (ECD) clinical trials",
        "Erdheim-Chester Disease (ECD) second opinion",
        "Erdheim-Chester Disease (ECD) prognosis",
        "Erdheim-Chester Disease (ECD) NCI cancer centers",
        "rare cancer care Erdheim-Chester Disease"
      ]
    }
  },
  {
    "slug": "blastic-plasmacytoid-dendritic-cell-neoplasm-bpdcn",
    "aliases": [
      "bpdcn"
    ],
    "path": "/rare-cancers/blastic-plasmacytoid-dendritic-cell-neoplasm-bpdcn",
    "name": "Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN)",
    "shortName": "Blastic Plasmacytoid Dendritic Cell Neoplasm",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Leukemia/Lymphoma",
    "description": "An aggressive haematological malignancy arising from plasmacytoid dendritic cell precursors. It characteristically presents with bruise-like skin lesions, then progresses to involve bone marrow, blood and lymph nodes. Diagnosis rests on the CD4+/CD56+/CD123+ immunophenotype.",
    "symptoms": [
      "Bruise-like or violaceous skin nodules and plaques",
      "Enlarged lymph nodes",
      "Enlarged spleen or liver",
      "Fatigue, bleeding or infection from low blood counts",
      "Central nervous system involvement in a significant minority"
    ],
    "treatmentOptions": [
      "Tagraxofusp (CD123-directed therapy, regulatory-approved for BPDCN)",
      "Intensive acute-leukaemia-style or lymphoma-style induction chemotherapy",
      "Central nervous system prophylaxis with intrathecal chemotherapy",
      "Allogeneic stem cell transplant in first remission for fit patients",
      "Venetoclax-containing combinations in older or unfit patients"
    ],
    "prognosis": "Historically poor, with median survival around 12-14 months. Outcomes are considerably better in patients who reach allogeneic transplant in first complete remission.",
    "researchStatus": "CD123-directed CAR-T cells, bispecific antibodies and venetoclax combinations are the leading research directions; capillary leak syndrome management with tagraxofusp is an active safety focus.",
    "molecularMarkers": [
      "CD123 (IL3RA) high",
      "CD4+",
      "CD56+",
      "TCF4",
      "TET2",
      "ASXL1",
      "MYB rearrangement"
    ],
    "whoItAffects": "Older adults, median around 65-70; strong male predominance",
    "incidence": "Approximately 0.04 per 100,000 per year",
    "redFlags": [
      "Rapidly appearing bruise-like skin lesions with cytopenias"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Naveen Pemmaraju",
        "title": "Hematologist-Oncologist",
        "institution": "MD Anderson",
        "city": "Houston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 10,
        "specialties": [
          "Hematology-Oncology",
          "BPDCN",
          "Clinical Research"
        ]
      },
      {
        "name": "Prof. Chng Wee Joo",
        "title": "Senior Hematologist",
        "institution": "NUH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 40,
        "specialties": [
          "Hematology",
          "Rare Leukemias",
          "BPDCN"
        ]
      },
      {
        "name": "Prof. Pier Luigi Zinzani",
        "title": "Hematologist",
        "institution": "University of Bologna",
        "city": "Bologna",
        "region": "Europe",
        "country": "Italy",
        "experienceYears": 30,
        "specialties": [
          "Lymphoid Malignancies",
          "BPDCN",
          "Expert Panels"
        ]
      },
      {
        "name": "Dr. Lalit Kumar",
        "title": "Medical Oncologist, Former Head of Medical Oncology",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Hemato-Oncology",
          "Blood Cancers",
          "BPDCN",
          "Bone Marrow Transplant"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) and how is it diagnosed?",
        "answer": "An aggressive haematological malignancy arising from plasmacytoid dendritic cell precursors. It characteristically presents with bruise-like skin lesions, then progresses to involve bone marrow, blood and lymph nodes. Diagnosis rests on the CD4+/CD56+/CD123+ immunophenotype. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN)?",
        "answer": "Standard first-line protocols include: Tagraxofusp (CD123-directed therapy, regulatory-approved for BPDCN), Intensive acute-leukaemia-style or lymphoma-style induction chemotherapy, Central nervous system prophylaxis with intrathecal chemotherapy. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: CD123 (IL3RA) high, CD4+, CD56+, TCF4, TET2, ASXL1, MYB rearrangement. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Rapidly appearing bruise-like skin lesions with cytopenias. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "CD123-directed CAR-T cells, bispecific antibodies and venetoclax combinations are the leading research directions; capillary leak syndrome management with tagraxofusp is an active safety focus. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/blastic-plasmacytoid-dendritic-cell-neoplasm-bpdcn",
      "keywords": [
        "Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) treatment",
        "Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) specialists",
        "Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) clinical trials",
        "Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) second opinion",
        "Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) prognosis",
        "Blastic Plasmacytoid Dendritic Cell Neoplasm (BPDCN) NCI cancer centers",
        "rare cancer care Blastic Plasmacytoid Dendritic Cell Neoplasm"
      ]
    }
  },
  {
    "slug": "t-cell-prolymphocytic-leukemia-t-pll",
    "aliases": [
      "t-pll",
      "tpll"
    ],
    "path": "/rare-cancers/t-cell-prolymphocytic-leukemia-t-pll",
    "name": "T-cell Prolymphocytic Leukemia (T-PLL)",
    "shortName": "T-cell Prolymphocytic Leukemia",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Leukemia",
    "description": "A rare and typically aggressive mature T-cell leukaemia with a very high white cell count, enlarged spleen and liver, and frequent skin involvement. It is defined by rearrangement of TCL1A and near-universal ATM abnormalities.",
    "symptoms": [
      "Markedly raised lymphocyte count found on a blood test",
      "Enlarged spleen causing early satiety and left-sided abdominal fullness",
      "Enlarged liver and lymph nodes",
      "Skin rash or infiltration, and pleural effusions",
      "Fatigue, anaemia and low platelets"
    ],
    "treatmentOptions": [
      "Alemtuzumab (anti-CD52), the most effective single agent",
      "Combination with pentostatin or fludarabine-based chemotherapy in selected patients",
      "Allogeneic stem cell transplant for consolidation in fit responders",
      "Watchful waiting in the rare truly indolent presentation",
      "Clinical trial enrolment at first relapse"
    ],
    "prognosis": "Poor without transplant, with median survival historically 1-2 years. Alemtuzumab produces high response rates but responses are rarely durable without allogeneic transplant consolidation.",
    "researchStatus": "BCL2 inhibition (venetoclax) combined with JAK/STAT inhibitors, and targeting the ATM-deficient state, are the most promising directions; an international T-PLL consortium sets response criteria.",
    "molecularMarkers": [
      "inv(14)(q11q32) or t(14;14) - TCL1A",
      "ATM deletion/mutation",
      "JAK3/STAT5B",
      "MTCP1"
    ],
    "whoItAffects": "Older adults, median around 65; male predominance",
    "incidence": "Approximately 0.6 per million per year",
    "redFlags": [],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Susan O'Brien",
        "title": "Hematologist",
        "institution": "UC Irvine (formerly MD Anderson)",
        "city": "Irvine",
        "region": "USA",
        "country": "USA",
        "experienceYears": 30,
        "specialties": [
          "Hematology",
          "CLL/T-PLL",
          "Alemtuzumab Trials"
        ]
      },
      {
        "name": "Dr. Daryl Tan",
        "title": "Hematologist",
        "institution": "SGH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Hematology",
          "Leukemias/Lymphomas",
          "T-PLL"
        ]
      },
      {
        "name": "Dr. Hugues de Th\u00e9",
        "title": "Hematologist",
        "institution": "H\u00f4pital Saint-Louis",
        "city": "Paris",
        "region": "Europe",
        "country": "France",
        "experienceYears": 35,
        "specialties": [
          "Hematology",
          "T-cell Leukemias",
          "T-PLL"
        ]
      },
      {
        "name": "Dr. Lalit Kumar",
        "title": "Medical Oncologist",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Hematologic Cancers",
          "Rare Leukemias",
          "T-PLL"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is T-cell Prolymphocytic Leukemia (T-PLL) and how is it diagnosed?",
        "answer": "A rare and typically aggressive mature T-cell leukaemia with a very high white cell count, enlarged spleen and liver, and frequent skin involvement. It is defined by rearrangement of TCL1A and near-universal ATM abnormalities. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for T-cell Prolymphocytic Leukemia (T-PLL)?",
        "answer": "Standard first-line protocols include: Alemtuzumab (anti-CD52), the most effective single agent, Combination with pentostatin or fludarabine-based chemotherapy in selected patients, Allogeneic stem cell transplant for consolidation in fit responders. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in T-cell Prolymphocytic Leukemia (T-PLL)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: inv(14)(q11q32) or t(14;14) - TCL1A, ATM deletion/mutation, JAK3/STAT5B, MTCP1. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "BCL2 inhibition (venetoclax) combined with JAK/STAT inhibitors, and targeting the ATM-deficient state, are the most promising directions; an international T-PLL consortium sets response criteria. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for T-cell Prolymphocytic Leukemia (T-PLL) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "T-cell Prolymphocytic Leukemia (T-PLL) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to T-cell Prolymphocytic Leukemia (T-PLL). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/t-cell-prolymphocytic-leukemia-t-pll",
      "keywords": [
        "T-cell Prolymphocytic Leukemia (T-PLL) treatment",
        "T-cell Prolymphocytic Leukemia (T-PLL) specialists",
        "T-cell Prolymphocytic Leukemia (T-PLL) clinical trials",
        "T-cell Prolymphocytic Leukemia (T-PLL) second opinion",
        "T-cell Prolymphocytic Leukemia (T-PLL) prognosis",
        "T-cell Prolymphocytic Leukemia (T-PLL) NCI cancer centers",
        "rare cancer care T-cell Prolymphocytic Leukemia"
      ]
    }
  },
  {
    "slug": "hepatosplenic-t-cell-lymphoma-hstcl",
    "aliases": [
      "hstcl"
    ],
    "path": "/rare-cancers/hepatosplenic-t-cell-lymphoma-hstcl",
    "name": "Hepatosplenic T-cell Lymphoma (HSTCL)",
    "shortName": "Hepatosplenic T-cell Lymphoma",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Lymphoma",
    "description": "A rare and aggressive lymphoma of gamma-delta T cells that infiltrates the liver, spleen and bone marrow sinusoids without forming enlarged lymph nodes. A recognised subset arises in people on long-term immunosuppression, particularly thiopurines with anti-TNF therapy for inflammatory bowel disease.",
    "symptoms": [
      "Marked enlargement of the spleen and liver",
      "Fever, night sweats and weight loss",
      "Low platelets, anaemia and low white cells",
      "Abdominal pain and fullness",
      "Notably absent peripheral lymph node enlargement"
    ],
    "treatmentOptions": [
      "Intensive induction chemotherapy (ICE, IVAC or hyper-CVAD style regimens)",
      "Allogeneic stem cell transplant in first remission - the only curative route",
      "Splenectomy in selected cases for diagnosis or symptom control",
      "Withdrawal of the implicated immunosuppressant where relevant",
      "Clinical trial enrolment"
    ],
    "prognosis": "Poor, with median survival historically under two years using conventional chemotherapy alone. Patients who proceed to allogeneic transplant in first remission have the best reported long-term outcomes.",
    "researchStatus": "Epigenetic agents including romidepsin and azacitidine, and JAK/STAT pathway inhibition, are being explored given the recurrent SETD2 and STAT5B lesions.",
    "molecularMarkers": [
      "Isochromosome 7q",
      "Trisomy 8",
      "SETD2",
      "STAT5B",
      "INO80",
      "gamma-delta TCR"
    ],
    "whoItAffects": "Adolescents and young adults, median around 35; strong male predominance",
    "incidence": "Fewer than 1 per million per year",
    "redFlags": [
      "Hepatosplenomegaly with cytopenias and no lymphadenopathy",
      "History of long-term thiopurine plus anti-TNF therapy"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Steven Horwitz",
        "title": "Medical Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "T-cell Lymphoma",
          "HSTCL",
          "Novel Immunotherapies"
        ]
      },
      {
        "name": "Dr. Lim Soon Thye",
        "title": "Medical Oncologist",
        "institution": "NCCS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 25,
        "specialties": [
          "Lymphoma Service",
          "T-cell Lymphomas",
          "HSTCL"
        ]
      },
      {
        "name": "Prof. Marie Jos\u00e9 Kersten",
        "title": "Hematologist",
        "institution": "NKI/AVL",
        "city": "Amsterdam",
        "region": "Europe",
        "country": "Netherlands",
        "experienceYears": 20,
        "specialties": [
          "T-cell Lymphomas",
          "HSTCL",
          "European Outcomes"
        ]
      },
      {
        "name": "Dr. Lalit Kumar",
        "title": "Medical Oncologist, Former Head of Medical Oncology",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Hemato-Oncology",
          "Blood Cancers",
          "HSTCL",
          "T-cell Lymphomas"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Hepatosplenic T-cell Lymphoma (HSTCL) and how is it diagnosed?",
        "answer": "A rare and aggressive lymphoma of gamma-delta T cells that infiltrates the liver, spleen and bone marrow sinusoids without forming enlarged lymph nodes. A recognised subset arises in people on long-term immunosuppression, particularly thiopurines with anti-TNF therapy for inflammatory bowel disease. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Hepatosplenic T-cell Lymphoma (HSTCL)?",
        "answer": "Standard first-line protocols include: Intensive induction chemotherapy (ICE, IVAC or hyper-CVAD style regimens), Allogeneic stem cell transplant in first remission - the only curative route, Splenectomy in selected cases for diagnosis or symptom control. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Hepatosplenic T-cell Lymphoma (HSTCL)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: Isochromosome 7q, Trisomy 8, SETD2, STAT5B, INO80, gamma-delta TCR. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Hepatosplenomegaly with cytopenias and no lymphadenopathy; History of long-term thiopurine plus anti-TNF therapy. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Epigenetic agents including romidepsin and azacitidine, and JAK/STAT pathway inhibition, are being explored given the recurrent SETD2 and STAT5B lesions. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Hepatosplenic T-cell Lymphoma (HSTCL) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Hepatosplenic T-cell Lymphoma (HSTCL) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Hepatosplenic T-cell Lymphoma (HSTCL). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/hepatosplenic-t-cell-lymphoma-hstcl",
      "keywords": [
        "Hepatosplenic T-cell Lymphoma (HSTCL) treatment",
        "Hepatosplenic T-cell Lymphoma (HSTCL) specialists",
        "Hepatosplenic T-cell Lymphoma (HSTCL) clinical trials",
        "Hepatosplenic T-cell Lymphoma (HSTCL) second opinion",
        "Hepatosplenic T-cell Lymphoma (HSTCL) prognosis",
        "Hepatosplenic T-cell Lymphoma (HSTCL) NCI cancer centers",
        "rare cancer care Hepatosplenic T-cell Lymphoma"
      ]
    }
  },
  {
    "slug": "malignant-rhabdoid-tumor",
    "aliases": [
      "mrt",
      "rhabdoid-tumor"
    ],
    "path": "/rare-cancers/malignant-rhabdoid-tumor",
    "name": "Malignant Rhabdoid Tumor",
    "shortName": "Malignant Rhabdoid Tumor",
    "category": "ultra-rare",
    "categoryLabel": "Ultra-Rare Malignancy (< 1 case per 1,000,000 / year)",
    "type": "Pediatric Tumor",
    "description": "An aggressive childhood cancer defined, like ATRT, by loss of SMARCB1 (INI1). When it arises outside the central nervous system it most often affects the kidney or soft tissue. A significant minority of children carry a germline SMARCB1 alteration (rhabdoid tumour predisposition syndrome), which makes genetic counselling part of standard care.",
    "symptoms": [
      "Abdominal mass or swelling",
      "Blood in the urine with kidney tumours",
      "Fever and irritability",
      "Pain at the tumour site",
      "Failure to thrive in infants"
    ],
    "treatmentOptions": [
      "Surgical resection where feasible",
      "Intensive multi-agent chemotherapy",
      "Radiotherapy, age-adapted",
      "High-dose chemotherapy with stem cell rescue in selected protocols",
      "Germline SMARCB1 testing and family genetic counselling"
    ],
    "prognosis": "Poor, particularly in infants under one year and in metastatic disease. Complete resection and older age at diagnosis are the strongest favourable factors.",
    "researchStatus": "EZH2 inhibition (tazemetostat) is approved in some rhabdoid contexts and under continued study; CDK4/6 inhibitors and immunotherapy combinations are in trials.",
    "molecularMarkers": [
      "SMARCB1 (INI1) biallelic loss",
      "Germline SMARCB1 in about 25-35%"
    ],
    "whoItAffects": "Infants and young children, median under 2 years",
    "incidence": "Approximately 0.6 per million children per year",
    "redFlags": [
      "Any rhabdoid tumour - test germline SMARCB1 and refer for counselling"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Jack Su",
        "title": "Pediatric Oncologist",
        "institution": "Texas Children's Hospital",
        "city": "Houston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 15,
        "specialties": [
          "Pediatric Oncology",
          "MRT",
          "INI1-targeted Therapies"
        ]
      },
      {
        "name": "Dr. Ann-Marie Teo",
        "title": "Pediatric Oncologist",
        "institution": "KK Women's and Children's Hospital (KKH)",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Pediatric Oncology",
          "MRT",
          "Multidisciplinary Care"
        ]
      },
      {
        "name": "Dr. Dominique Valteau-Couanet",
        "title": "Pediatric Oncologist",
        "institution": "Gustave Roussy",
        "city": "Villejuif",
        "region": "Europe",
        "country": "France",
        "experienceYears": 30,
        "specialties": [
          "Pediatric Solid Tumors",
          "MRT",
          "Intensive Multimodal Regimens"
        ]
      },
      {
        "name": "Dr. Girish Chinnaswamy",
        "title": "Professor & Head, Pediatric Oncology",
        "institution": "Tata Memorial Centre",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Pediatric Oncology",
          "MRT",
          "Intensive Multimodal Therapy"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Malignant Rhabdoid Tumor and how is it diagnosed?",
        "answer": "An aggressive childhood cancer defined, like ATRT, by loss of SMARCB1 (INI1). When it arises outside the central nervous system it most often affects the kidney or soft tissue. A significant minority of children carry a germline SMARCB1 alteration (rhabdoid tumour predisposition syndrome), which makes genetic counselling part of standard care. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Malignant Rhabdoid Tumor?",
        "answer": "Standard first-line protocols include: Surgical resection where feasible, Intensive multi-agent chemotherapy, Radiotherapy, age-adapted. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Malignant Rhabdoid Tumor?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: SMARCB1 (INI1) biallelic loss, Germline SMARCB1 in about 25-35%. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Any rhabdoid tumour - test germline SMARCB1 and refer for counselling. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "EZH2 inhibition (tazemetostat) is approved in some rhabdoid contexts and under continued study; CDK4/6 inhibitors and immunotherapy combinations are in trials. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Malignant Rhabdoid Tumor through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Malignant Rhabdoid Tumor Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Malignant Rhabdoid Tumor. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/malignant-rhabdoid-tumor",
      "keywords": [
        "Malignant Rhabdoid Tumor treatment",
        "Malignant Rhabdoid Tumor specialists",
        "Malignant Rhabdoid Tumor clinical trials",
        "Malignant Rhabdoid Tumor second opinion",
        "Malignant Rhabdoid Tumor prognosis",
        "Malignant Rhabdoid Tumor NCI cancer centers",
        "rare cancer care Malignant Rhabdoid Tumor"
      ]
    }
  },
  {
    "slug": "alveolar-soft-part-sarcoma-asps",
    "aliases": [
      "asps",
      "alveolar-soft-part-sarcoma"
    ],
    "path": "/rare-cancers/alveolar-soft-part-sarcoma-asps",
    "name": "Alveolar Soft Part Sarcoma (ASPS)",
    "shortName": "Alveolar Soft Part Sarcoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Sarcoma",
    "description": "A slow-growing but persistently metastasising soft-tissue sarcoma driven by the ASPSCR1-TFE3 fusion. It is unusual among sarcomas in that patients often live for years even with metastatic disease, and in that it responds to immunotherapy rather than conventional chemotherapy.",
    "symptoms": [
      "A painless, slowly enlarging soft-tissue lump, often in the thigh or buttock",
      "In children, a mass in the head and neck including the tongue or orbit",
      "Symptoms from lung metastases - cough or breathlessness",
      "Headache or seizures from brain metastases",
      "Often found incidentally; metastases may be the first presentation"
    ],
    "treatmentOptions": [
      "Wide surgical excision of the primary tumour",
      "Atezolizumab (regulatory-approved for advanced ASPS)",
      "Antiangiogenic tyrosine kinase inhibitors - cediranib, sunitinib, pazopanib",
      "Metastasectomy for limited lung disease",
      "Radiotherapy for local control or symptomatic metastases"
    ],
    "prognosis": "Better than most metastatic sarcomas despite frequent spread. Five-year survival with localised disease is high; with metastases, multi-year survival is common and has improved further with immunotherapy.",
    "researchStatus": "Checkpoint inhibitor combinations with antiangiogenics, and direct TFE3 fusion targeting, are the leading directions. Brain metastasis surveillance is an area of evolving guidance.",
    "molecularMarkers": [
      "ASPSCR1(ASPL)-TFE3 fusion, der(17)t(X;17)(p11;q25)",
      "TFE3 nuclear positivity by IHC"
    ],
    "whoItAffects": "Adolescents and young adults, median 20-30; slight female predominance",
    "incidence": "Approximately 1% of soft-tissue sarcomas; under 1 per million per year",
    "redFlags": [
      "Consider baseline brain imaging - brain metastases are unusually common"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Breelyn Wilky",
        "title": "Sarcoma Oncologist",
        "institution": "UCHealth",
        "city": "Denver",
        "region": "USA",
        "country": "USA",
        "experienceYears": 10,
        "specialties": [
          "Sarcoma Oncology",
          "ASPS",
          "TKI Trials"
        ]
      },
      {
        "name": "Dr. Richard Quek",
        "title": "Senior Consultant Medical Oncologist",
        "institution": "Sarcoma Specialist",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 16,
        "specialties": [
          "Sarcoma Oncology",
          "ASPS",
          "Targeted Therapies"
        ]
      },
      {
        "name": "Dr. Peter Hohenberger",
        "title": "Surgical Oncologist",
        "institution": "University Hospital Mannheim",
        "city": "Mannheim",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 25,
        "specialties": [
          "Soft-tissue Sarcomas",
          "ASPS",
          "Surgical Resection"
        ]
      },
      {
        "name": "Dr. Ashish Gulia",
        "title": "Orthopedic Oncologist",
        "institution": "Tata Memorial",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Orthopedic Oncology",
          "ASPS",
          "Sarcoma Guidelines"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Alveolar Soft Part Sarcoma (ASPS) and how is it diagnosed?",
        "answer": "A slow-growing but persistently metastasising soft-tissue sarcoma driven by the ASPSCR1-TFE3 fusion. It is unusual among sarcomas in that patients often live for years even with metastatic disease, and in that it responds to immunotherapy rather than conventional chemotherapy. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Alveolar Soft Part Sarcoma (ASPS)?",
        "answer": "Standard first-line protocols include: Wide surgical excision of the primary tumour, Atezolizumab (regulatory-approved for advanced ASPS), Antiangiogenic tyrosine kinase inhibitors - cediranib, sunitinib, pazopanib. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Alveolar Soft Part Sarcoma (ASPS)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: ASPSCR1(ASPL)-TFE3 fusion, der(17)t(X;17)(p11;q25), TFE3 nuclear positivity by IHC. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Consider baseline brain imaging - brain metastases are unusually common. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Checkpoint inhibitor combinations with antiangiogenics, and direct TFE3 fusion targeting, are the leading directions. Brain metastasis surveillance is an area of evolving guidance. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Alveolar Soft Part Sarcoma (ASPS) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Alveolar Soft Part Sarcoma (ASPS) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Alveolar Soft Part Sarcoma (ASPS). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/alveolar-soft-part-sarcoma-asps",
      "keywords": [
        "Alveolar Soft Part Sarcoma (ASPS) treatment",
        "Alveolar Soft Part Sarcoma (ASPS) specialists",
        "Alveolar Soft Part Sarcoma (ASPS) clinical trials",
        "Alveolar Soft Part Sarcoma (ASPS) second opinion",
        "Alveolar Soft Part Sarcoma (ASPS) prognosis",
        "Alveolar Soft Part Sarcoma (ASPS) NCI cancer centers",
        "rare cancer care Alveolar Soft Part Sarcoma"
      ]
    }
  },
  {
    "slug": "clear-cell-sarcoma",
    "aliases": [
      "clear-cell-sarcoma",
      "ccs"
    ],
    "path": "/rare-cancers/clear-cell-sarcoma",
    "name": "Clear Cell Sarcoma",
    "shortName": "Clear Cell Sarcoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Sarcoma",
    "description": "A rare soft-tissue sarcoma of tendons and aponeuroses, historically called melanoma of soft parts because it produces melanin and expresses melanocytic markers. It is nonetheless a sarcoma, defined by the EWSR1-ATF1 fusion, and unlike cutaneous melanoma it responds poorly to checkpoint inhibitors.",
    "symptoms": [
      "A slowly growing, deep-seated lump near a tendon, usually foot, ankle or knee",
      "Pain or tenderness in about half of patients",
      "Restricted joint movement",
      "Regional lymph node enlargement - nodal spread is characteristic",
      "Long history before diagnosis is common"
    ],
    "treatmentOptions": [
      "Wide surgical excision with negative margins - the mainstay",
      "Sentinel node biopsy or regional lymph node assessment",
      "Adjuvant radiotherapy for close margins or large tumours",
      "MET inhibitors and antiangiogenic TKIs in advanced disease (trial setting)",
      "Conventional chemotherapy has limited activity"
    ],
    "prognosis": "Guarded. Five-year survival is roughly 50-65% overall but falls substantially with tumours over 5 cm, necrosis or nodal involvement. Late relapse beyond ten years is well described, so surveillance must be long.",
    "researchStatus": "MET pathway inhibition, given ATF1-driven MET upregulation, and EWSR1-ATF1-directed approaches are the principal research strategies.",
    "molecularMarkers": [
      "EWSR1-ATF1 fusion, t(12;22)(q13;q12)",
      "EWSR1-CREB1 - variant",
      "S100/HMB45/MelanA positive"
    ],
    "whoItAffects": "Young adults, typically 20-40 years",
    "incidence": "Under 1% of soft-tissue sarcomas",
    "redFlags": [],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Mark Dickson",
        "title": "Medical Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 15,
        "specialties": [
          "Rare Sarcomas",
          "Clear Cell Sarcoma",
          "Immunotherapy"
        ]
      },
      {
        "name": "Dr. Wong Seng Weng",
        "title": "Medical Oncologist",
        "institution": "Parkway Cancer Centre",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Melanocytic Clear Cell Sarcoma",
          "Novel Agents",
          "Sarcoma"
        ]
      },
      {
        "name": "Prof. Paolo Casali",
        "title": "Medical Oncologist",
        "institution": "Istituto Nazionale Tumori (INT)",
        "city": "Milan",
        "region": "Europe",
        "country": "Italy",
        "experienceYears": 30,
        "specialties": [
          "Sarcoma Oncology",
          "Clear Cell Sarcoma",
          "Systemic Therapy"
        ]
      },
      {
        "name": "Dr. Sameer Rastogi",
        "title": "Sarcoma Medical Oncologist",
        "institution": "AIIMS, New Delhi",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Sarcoma Clinic",
          "Clear Cell Sarcoma",
          "Targeted Therapy"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Clear Cell Sarcoma and how is it diagnosed?",
        "answer": "A rare soft-tissue sarcoma of tendons and aponeuroses, historically called melanoma of soft parts because it produces melanin and expresses melanocytic markers. It is nonetheless a sarcoma, defined by the EWSR1-ATF1 fusion, and unlike cutaneous melanoma it responds poorly to checkpoint inhibitors. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Clear Cell Sarcoma?",
        "answer": "Standard first-line protocols include: Wide surgical excision with negative margins - the mainstay, Sentinel node biopsy or regional lymph node assessment, Adjuvant radiotherapy for close margins or large tumours. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Clear Cell Sarcoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: EWSR1-ATF1 fusion, t(12;22)(q13;q12), EWSR1-CREB1 - variant, S100/HMB45/MelanA positive. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "MET pathway inhibition, given ATF1-driven MET upregulation, and EWSR1-ATF1-directed approaches are the principal research strategies. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Clear Cell Sarcoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Clear Cell Sarcoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Clear Cell Sarcoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/clear-cell-sarcoma",
      "keywords": [
        "Clear Cell Sarcoma treatment",
        "Clear Cell Sarcoma specialists",
        "Clear Cell Sarcoma clinical trials",
        "Clear Cell Sarcoma second opinion",
        "Clear Cell Sarcoma prognosis",
        "Clear Cell Sarcoma NCI cancer centers",
        "rare cancer care Clear Cell Sarcoma"
      ]
    }
  },
  {
    "slug": "angiosarcoma",
    "aliases": [
      "angiosarcoma"
    ],
    "path": "/rare-cancers/angiosarcoma",
    "name": "Angiosarcoma",
    "shortName": "Angiosarcoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Sarcoma",
    "description": "A malignant tumour of the cells lining blood and lymphatic vessels. It occurs in several distinct settings: on the scalp and face of older adults, in the breast after radiotherapy, in long-standing lymphoedema after breast cancer surgery (Stewart-Treves syndrome), and in deep viscera such as the liver, heart and spleen.",
    "symptoms": [
      "A bruise-like patch on the scalp or face that will not resolve",
      "A rapidly enlarging, sometimes bleeding, skin lesion",
      "New skin change in a previously irradiated field",
      "Swelling, pain or a mass in a chronically lymphoedematous limb",
      "Anaemia, abdominal pain or spontaneous bleeding with visceral disease"
    ],
    "treatmentOptions": [
      "Wide surgical excision - margins are frequently difficult on the scalp",
      "Radiotherapy, often large-field, for cutaneous scalp disease",
      "Weekly paclitaxel - the most consistently active systemic agent",
      "Doxorubicin-based chemotherapy",
      "Antiangiogenic agents (pazopanib, bevacizumab) and propranolol in selected settings",
      "Immunotherapy in UV-signature cutaneous head and neck disease"
    ],
    "prognosis": "Generally poor, with five-year survival around 30-40% and worse for visceral, cardiac and radiation-associated disease. Small, completely resected cutaneous lesions do considerably better.",
    "researchStatus": "Checkpoint inhibitors show real activity in the high-mutational-burden UV-driven cutaneous subtype; MYC-amplified radiation-associated disease is being studied separately as a distinct entity.",
    "molecularMarkers": [
      "MYC amplification (radiation/lymphoedema-associated)",
      "KDR",
      "PLCG1",
      "PTPRB",
      "TP53"
    ],
    "whoItAffects": "Older adults for cutaneous disease; any age for visceral or secondary forms",
    "incidence": "Approximately 1-2% of soft-tissue sarcomas; roughly 3 per million per year",
    "redFlags": [
      "Any non-healing bruise-like scalp lesion in an older adult - biopsy"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Sandra D'Angelo",
        "title": "Medical Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 15,
        "specialties": [
          "Angiosarcoma",
          "Immunotherapy Combinations",
          "Clinical Trials"
        ]
      },
      {
        "name": "Dr. Toh Wei Peng",
        "title": "Surgical Oncologist",
        "institution": "National Cancer Centre",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Surgical Oncology",
          "Head/Neck Angiosarcoma",
          "Multimodal Therapy"
        ]
      },
      {
        "name": "Prof. Henrik Falch",
        "title": "Surgical Oncologist",
        "institution": "University Hospital Copenhagen",
        "city": "Copenhagen",
        "region": "Europe",
        "country": "Denmark",
        "experienceYears": 20,
        "specialties": [
          "Cutaneous Angiosarcoma",
          "Surgery",
          "Intralesional Therapy"
        ]
      },
      {
        "name": "Dr. Navin Khattry",
        "title": "Medical Oncologist",
        "institution": "ACTREC",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Rare Sarcomas",
          "Angiosarcoma",
          "Metronomic Regimens"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Angiosarcoma and how is it diagnosed?",
        "answer": "A malignant tumour of the cells lining blood and lymphatic vessels. It occurs in several distinct settings: on the scalp and face of older adults, in the breast after radiotherapy, in long-standing lymphoedema after breast cancer surgery (Stewart-Treves syndrome), and in deep viscera such as the liver, heart and spleen. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Angiosarcoma?",
        "answer": "Standard first-line protocols include: Wide surgical excision - margins are frequently difficult on the scalp, Radiotherapy, often large-field, for cutaneous scalp disease, Weekly paclitaxel - the most consistently active systemic agent. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Angiosarcoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: MYC amplification (radiation/lymphoedema-associated), KDR, PLCG1, PTPRB, TP53. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Any non-healing bruise-like scalp lesion in an older adult - biopsy. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Checkpoint inhibitors show real activity in the high-mutational-burden UV-driven cutaneous subtype; MYC-amplified radiation-associated disease is being studied separately as a distinct entity. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Angiosarcoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Angiosarcoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Angiosarcoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/angiosarcoma",
      "keywords": [
        "Angiosarcoma treatment",
        "Angiosarcoma specialists",
        "Angiosarcoma clinical trials",
        "Angiosarcoma second opinion",
        "Angiosarcoma prognosis",
        "Angiosarcoma NCI cancer centers",
        "rare cancer care Angiosarcoma"
      ]
    }
  },
  {
    "slug": "chordoma",
    "aliases": [
      "chordoma"
    ],
    "path": "/rare-cancers/chordoma",
    "name": "Chordoma",
    "shortName": "Chordoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Bone Tumor",
    "description": "A slow-growing bone tumour arising from remnants of the embryonic notochord, occurring along the midline of the skull base, mobile spine and sacrum. It grows insidiously and encases critical nerves and vessels, which makes complete removal the single most important - and most technically demanding - determinant of outcome.",
    "symptoms": [
      "Headache, double vision or difficulty swallowing with skull-base tumours",
      "Low back or tailbone pain with sacral tumours, often for years",
      "Bowel or bladder dysfunction",
      "Numbness, weakness or sciatica-like pain",
      "A palpable presacral mass on rectal examination"
    ],
    "treatmentOptions": [
      "En bloc surgical resection with wide margins - the primary determinant of cure",
      "Proton beam or carbon ion radiotherapy, which permit high doses near neural structures",
      "Conventional high-dose photon radiotherapy where particle therapy is unavailable",
      "Systemic therapy in advanced disease - imatinib, EGFR inhibitors (trial setting)",
      "Specialist multidisciplinary review before any biopsy, to avoid tract seeding"
    ],
    "prognosis": "Median survival is around 7-8 years, with five-year survival near 65-70%. Local recurrence, not distant spread, is the usual cause of death. Outcomes are markedly better at high-volume centres.",
    "researchStatus": "Brachyury (TBXT) is the lineage-defining driver and is the target of vaccine and degrader programmes; the Chordoma Foundation coordinates an international trial network.",
    "molecularMarkers": [
      "Brachyury (TBXT) nuclear expression",
      "TBXT duplication",
      "CDKN2A loss",
      "SMARCB1 loss - poorly differentiated variant"
    ],
    "whoItAffects": "Adults, median 50-60; the poorly differentiated form affects children",
    "incidence": "Approximately 0.8 per million per year",
    "redFlags": [
      "Do not biopsy a suspected chordoma outside a sarcoma centre - seeding risk"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "RED_FLAGS_ONLY",
    "specialists": [
      {
        "name": "Dr. Ian F. Pollack",
        "title": "Neurosurgeon",
        "institution": "University of Pittsburgh",
        "city": "Pittsburgh",
        "region": "USA",
        "country": "USA",
        "experienceYears": 30,
        "specialties": [
          "Skull-base Surgery",
          "Chordoma",
          "Proton Therapy"
        ]
      },
      {
        "name": "Dr. Iqbal Muttaqin",
        "title": "Neurosurgeon",
        "institution": "NCCS Singapore",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Spine/Skull-base Tumors",
          "Chordoma",
          "Advanced Techniques"
        ]
      },
      {
        "name": "Prof. Ralph Weber",
        "title": "Orthopedic Surgeon",
        "institution": "Charit\u00e9 Berlin",
        "city": "Berlin",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 25,
        "specialties": [
          "Chordoma Resection",
          "Sacral/Pelvic Sites",
          "Proton Centers"
        ]
      },
      {
        "name": "Dr. S. Karunakaran",
        "title": "Neurosurgeon",
        "institution": "Apollo Hospitals",
        "city": "Chennai",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Skull-base Surgery",
          "Clival/Sacral Chordoma",
          "Proton Therapy"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Chordoma and how is it diagnosed?",
        "answer": "A slow-growing bone tumour arising from remnants of the embryonic notochord, occurring along the midline of the skull base, mobile spine and sacrum. It grows insidiously and encases critical nerves and vessels, which makes complete removal the single most important - and most technically demanding - determinant of outcome. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Chordoma?",
        "answer": "Standard first-line protocols include: En bloc surgical resection with wide margins - the primary determinant of cure, Proton beam or carbon ion radiotherapy, which permit high doses near neural structures, Conventional high-dose photon radiotherapy where particle therapy is unavailable. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Chordoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: Brachyury (TBXT) nuclear expression, TBXT duplication, CDKN2A loss, SMARCB1 loss - poorly differentiated variant. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Do not biopsy a suspected chordoma outside a sarcoma centre - seeding risk. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Brachyury (TBXT) is the lineage-defining driver and is the target of vaccine and degrader programmes; the Chordoma Foundation coordinates an international trial network. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Chordoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Chordoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Chordoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/chordoma",
      "keywords": [
        "Chordoma treatment",
        "Chordoma specialists",
        "Chordoma clinical trials",
        "Chordoma second opinion",
        "Chordoma prognosis",
        "Chordoma NCI cancer centers",
        "rare cancer care Chordoma"
      ]
    }
  },
  {
    "slug": "adrenocortical-carcinoma-acc",
    "aliases": [
      "adrenocortical-carcinoma",
      "adrenal-cancer",
      "adrenocortical-cancer"
    ],
    "path": "/rare-cancers/adrenocortical-carcinoma-acc",
    "name": "Adrenocortical Carcinoma (ACC)",
    "shortName": "Adrenocortical Carcinoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Adrenal Cancer",
    "description": "A rare cancer of the adrenal cortex. Around 60% are functional, secreting cortisol, androgens or both, so patients may present with hormonal syndromes before any mass effect. It has a bimodal age distribution with a small childhood peak, and childhood cases carry a high rate of germline TP53 alteration.",
    "symptoms": [
      "Rapid weight gain, round face, purple stretch marks and muscle weakness (cortisol excess)",
      "New facial hair, acne, deepening voice or menstrual disturbance in women (androgen excess)",
      "Flank or abdominal pain and a palpable mass",
      "New or worsening high blood pressure and low potassium",
      "Precocious puberty in children"
    ],
    "treatmentOptions": [
      "Complete open surgical resection by a high-volume adrenal surgeon",
      "Adjuvant mitotane for high-risk resected disease",
      "EDP-M chemotherapy (etoposide, doxorubicin, cisplatin plus mitotane) for advanced disease",
      "Radiotherapy to the tumour bed in selected high-risk cases",
      "Blockade of steroid excess - metyrapone, ketoconazole - as supportive care"
    ],
    "prognosis": "Stage-dependent and generally guarded. Five-year survival is roughly 60-80% for completely resected localised disease but under 15% for metastatic disease. Ki-67 index and resection margin status are the strongest pathological predictors.",
    "researchStatus": "Immunotherapy has shown modest single-agent activity; IGF1R inhibition was unsuccessful in phase III. Current focus is on combination immunotherapy, and on trials stratified by the recognised molecular subgroups from comprehensive genomic profiling.",
    "molecularMarkers": [
      "TP53 (including germline - Li-Fraumeni)",
      "CTNNB1",
      "ZNRF3",
      "IGF2 overexpression",
      "Ki-67 index"
    ],
    "whoItAffects": "Bimodal - children under 5 and adults 40-50; female predominance in adults",
    "incidence": "Approximately 0.7-2 per million per year",
    "redFlags": [
      "Paediatric ACC - test germline TP53 and refer for genetic counselling"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Gary D. Hammer",
        "title": "Endocrine Oncologist",
        "institution": "University of Michigan",
        "city": "Ann Arbor",
        "region": "USA",
        "country": "USA",
        "experienceYears": 25,
        "specialties": [
          "Endocrine Oncology",
          "ACC",
          "Clinical Trials"
        ]
      },
      {
        "name": "Dr. Khoo Kei Siong",
        "title": "Medical Oncologist",
        "institution": "Parkway Cancer Centre",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 30,
        "specialties": [
          "Medical Oncology",
          "ACC",
          "Mitotane Therapy"
        ]
      },
      {
        "name": "Prof. Martin Fassnacht",
        "title": "Endocrinologist",
        "institution": "University of W\u00fcrzburg",
        "city": "W\u00fcrzburg",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 20,
        "specialties": [
          "Endocrinology",
          "ACC Research",
          "European Guidelines"
        ]
      },
      {
        "name": "Dr. Rajeev Agarwal",
        "title": "Surgical Oncologist",
        "institution": "Private Practice",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Adrenal Tumor Surgery",
          "ACC",
          "Multidisciplinary Management"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Adrenocortical Carcinoma (ACC) and how is it diagnosed?",
        "answer": "A rare cancer of the adrenal cortex. Around 60% are functional, secreting cortisol, androgens or both, so patients may present with hormonal syndromes before any mass effect. It has a bimodal age distribution with a small childhood peak, and childhood cases carry a high rate of germline TP53 alteration. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Adrenocortical Carcinoma (ACC)?",
        "answer": "Standard first-line protocols include: Complete open surgical resection by a high-volume adrenal surgeon, Adjuvant mitotane for high-risk resected disease, EDP-M chemotherapy (etoposide, doxorubicin, cisplatin plus mitotane) for advanced disease. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Adrenocortical Carcinoma (ACC)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: TP53 (including germline - Li-Fraumeni), CTNNB1, ZNRF3, IGF2 overexpression, Ki-67 index. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Paediatric ACC - test germline TP53 and refer for genetic counselling. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Immunotherapy has shown modest single-agent activity; IGF1R inhibition was unsuccessful in phase III. Current focus is on combination immunotherapy, and on trials stratified by the recognised molecular subgroups from comprehensive genomic profiling. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Adrenocortical Carcinoma (ACC) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Adrenocortical Carcinoma (ACC) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Adrenocortical Carcinoma (ACC). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/adrenocortical-carcinoma-acc",
      "keywords": [
        "Adrenocortical Carcinoma (ACC) treatment",
        "Adrenocortical Carcinoma (ACC) specialists",
        "Adrenocortical Carcinoma (ACC) clinical trials",
        "Adrenocortical Carcinoma (ACC) second opinion",
        "Adrenocortical Carcinoma (ACC) prognosis",
        "Adrenocortical Carcinoma (ACC) NCI cancer centers",
        "rare cancer care Adrenocortical Carcinoma"
      ]
    }
  },
  {
    "slug": "merkel-cell-carcinoma",
    "aliases": [
      "merkel-cell-carcinoma",
      "mcc"
    ],
    "path": "/rare-cancers/merkel-cell-carcinoma",
    "name": "Merkel Cell Carcinoma",
    "shortName": "Merkel Cell Carcinoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Skin Cancer",
    "description": "An aggressive neuroendocrine skin cancer with two distinct causes: integration of Merkel cell polyomavirus, which accounts for most cases, and cumulative ultraviolet damage in the remainder. It is far more likely than melanoma to have spread to lymph nodes at presentation despite a small and unremarkable-looking primary.",
    "symptoms": [
      "A firm, painless, rapidly growing red or violet nodule on sun-exposed skin",
      "Most often on the head, neck or forearm",
      "Enlarged regional lymph nodes",
      "Usually not ulcerated or itchy, which is why it is often dismissed as a cyst",
      "Occasionally presents as nodal disease with no identifiable primary"
    ],
    "treatmentOptions": [
      "Wide local excision with sentinel lymph node biopsy - mandatory even for small lesions",
      "Adjuvant radiotherapy to the primary site and draining nodal basin",
      "Immune checkpoint inhibitors - avelumab, pembrolizumab, retifanlimab - for advanced disease",
      "Chemotherapy (platinum-etoposide) where immunotherapy is contraindicated",
      "Reduction of immunosuppression where clinically possible"
    ],
    "prognosis": "Stage-dependent. Five-year survival is around 75% for localised disease but historically under 20% for metastatic disease - though checkpoint inhibitors have substantially improved this, with durable responses in a significant proportion of treated patients.",
    "researchStatus": "Neoadjuvant checkpoint blockade, combination immunotherapy for primary refractory disease, and MCPyV-specific T-cell therapies are the leading directions. Antibody titres to MCPyV oncoprotein are used for surveillance.",
    "molecularMarkers": [
      "Merkel cell polyomavirus (about 80%)",
      "High UV mutational burden in virus-negative disease",
      "RB1",
      "TP53",
      "CK20 dot-like positivity"
    ],
    "whoItAffects": "Older adults over 65, fair-skinned; markedly increased in immunosuppression",
    "incidence": "Approximately 0.7 per 100,000 per year and rising",
    "redFlags": [
      "The AEIOU features - Asymptomatic, Expanding rapidly, Immunosuppressed, Older than 50, UV-exposed fair skin"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Paul Nghiem",
        "title": "Dermatologic Oncologist",
        "institution": "UW/Fred Hutch",
        "city": "Seattle",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Dermatologic Oncology",
          "Merkel Cell Carcinoma",
          "Immunotherapy"
        ]
      },
      {
        "name": "Dr. Lee Soo Chin",
        "title": "Medical Oncologist",
        "institution": "NCIS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 25,
        "specialties": [
          "Medical Oncology",
          "Merkel Cell Carcinoma",
          "Checkpoint Inhibitors"
        ]
      },
      {
        "name": "Prof. Dirk Schadendorf",
        "title": "Dermatologic Oncologist",
        "institution": "University Hospital Essen",
        "city": "Essen",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 30,
        "specialties": [
          "Dermatologic Oncology",
          "Merkel Cell Carcinoma",
          "Clinical Trials"
        ]
      },
      {
        "name": "Dr. Nikhil Vasudev",
        "title": "Medical Oncologist",
        "institution": "Tata Memorial",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Medical Oncology",
          "Merkel Cell Carcinoma",
          "Rare Skin Cancers"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Merkel Cell Carcinoma and how is it diagnosed?",
        "answer": "An aggressive neuroendocrine skin cancer with two distinct causes: integration of Merkel cell polyomavirus, which accounts for most cases, and cumulative ultraviolet damage in the remainder. It is far more likely than melanoma to have spread to lymph nodes at presentation despite a small and unremarkable-looking primary. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Merkel Cell Carcinoma?",
        "answer": "Standard first-line protocols include: Wide local excision with sentinel lymph node biopsy - mandatory even for small lesions, Adjuvant radiotherapy to the primary site and draining nodal basin, Immune checkpoint inhibitors - avelumab, pembrolizumab, retifanlimab - for advanced disease. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Merkel Cell Carcinoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: Merkel cell polyomavirus (about 80%), High UV mutational burden in virus-negative disease, RB1, TP53, CK20 dot-like positivity. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: The AEIOU features - Asymptomatic, Expanding rapidly, Immunosuppressed, Older than 50, UV-exposed fair skin. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Neoadjuvant checkpoint blockade, combination immunotherapy for primary refractory disease, and MCPyV-specific T-cell therapies are the leading directions. Antibody titres to MCPyV oncoprotein are used for surveillance. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Merkel Cell Carcinoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Merkel Cell Carcinoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Merkel Cell Carcinoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/merkel-cell-carcinoma",
      "keywords": [
        "Merkel Cell Carcinoma treatment",
        "Merkel Cell Carcinoma specialists",
        "Merkel Cell Carcinoma clinical trials",
        "Merkel Cell Carcinoma second opinion",
        "Merkel Cell Carcinoma prognosis",
        "Merkel Cell Carcinoma NCI cancer centers",
        "rare cancer care Merkel Cell Carcinoma"
      ]
    }
  },
  {
    "slug": "uveal-melanoma",
    "aliases": [
      "uveal-melanoma",
      "ocular-melanoma",
      "eye-melanoma"
    ],
    "path": "/rare-cancers/uveal-melanoma",
    "name": "Uveal Melanoma",
    "shortName": "Uveal Melanoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Eye Cancer",
    "description": "The commonest primary eye cancer in adults, arising from pigment cells of the choroid, ciliary body or iris. It is biologically distinct from skin melanoma - different drivers, no BRAF V600E, and a strong tendency to spread to the liver, sometimes many years after the eye has been successfully treated.",
    "symptoms": [
      "Blurred vision or a shadow in the field of vision",
      "Flashing lights or floaters",
      "A dark spot on the iris that is enlarging",
      "Change in pupil shape",
      "Frequently asymptomatic and found on routine optometry"
    ],
    "treatmentOptions": [
      "Plaque brachytherapy - the commonest eye-preserving treatment",
      "Proton beam radiotherapy for larger or posteriorly located tumours",
      "Enucleation for large tumours or when vision is already lost",
      "Tebentafusp for HLA-A*02:01-positive metastatic disease",
      "Liver-directed therapy - hepatic perfusion, embolisation - for liver-limited spread",
      "Prognostic biopsy with gene expression profiling to guide surveillance intensity"
    ],
    "prognosis": "Local eye control is achieved in over 90% of cases, but roughly half of patients eventually develop metastases, overwhelmingly hepatic. Monosomy 3 and BAP1 loss identify the high-risk group. Tebentafusp is the first therapy to show an overall survival benefit in metastatic disease.",
    "researchStatus": "Tebentafusp in earlier lines, HLA-agnostic ImmTAC agents for the HLA-A*02:01-negative majority, and liver-directed combinations are the principal areas of development.",
    "molecularMarkers": [
      "GNAQ / GNA11",
      "BAP1 loss - high metastatic risk",
      "SF3B1",
      "EIF1AX - low risk",
      "Monosomy 3"
    ],
    "whoItAffects": "Adults, median around 60; light iris colour is a risk factor",
    "incidence": "Approximately 5-6 per million per year",
    "redFlags": [
      "Lifelong liver surveillance is required - metastasis can occur 10+ years later"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Takami Sato",
        "title": "Medical Oncologist",
        "institution": "Jefferson Hospital",
        "city": "Philadelphia",
        "region": "USA",
        "country": "USA",
        "experienceYears": 30,
        "specialties": [
          "Uveal Melanoma",
          "Hepatic Perfusion",
          "Clinical Trials"
        ]
      },
      {
        "name": "Dr. Adrian Tan",
        "title": "Senior Consultant Ophthalmologist",
        "institution": "SNEC",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Ocular Oncology",
          "Uveal Melanoma",
          "Plaque Brachytherapy"
        ]
      },
      {
        "name": "Prof. Sarah Coupland",
        "title": "Ocular Pathologist/Oncologist",
        "institution": "University of Liverpool",
        "city": "Liverpool",
        "region": "Europe",
        "country": "United Kingdom",
        "experienceYears": 25,
        "specialties": [
          "Ocular Oncology",
          "Uveal Melanoma Research",
          "Ocular Tumors"
        ]
      },
      {
        "name": "Dr. Santosh Honavar",
        "title": "Ocular Oncologist",
        "institution": "LV Prasad Eye Institute",
        "city": "Hyderabad",
        "region": "India",
        "country": "India",
        "experienceYears": 25,
        "specialties": [
          "Ocular Oncology",
          "Uveal Melanoma",
          "Plaque Radiotherapy"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Uveal Melanoma and how is it diagnosed?",
        "answer": "The commonest primary eye cancer in adults, arising from pigment cells of the choroid, ciliary body or iris. It is biologically distinct from skin melanoma - different drivers, no BRAF V600E, and a strong tendency to spread to the liver, sometimes many years after the eye has been successfully treated. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Uveal Melanoma?",
        "answer": "Standard first-line protocols include: Plaque brachytherapy - the commonest eye-preserving treatment, Proton beam radiotherapy for larger or posteriorly located tumours, Enucleation for large tumours or when vision is already lost. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Uveal Melanoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: GNAQ / GNA11, BAP1 loss - high metastatic risk, SF3B1, EIF1AX - low risk, Monosomy 3. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Lifelong liver surveillance is required - metastasis can occur 10+ years later. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Tebentafusp in earlier lines, HLA-agnostic ImmTAC agents for the HLA-A*02:01-negative majority, and liver-directed combinations are the principal areas of development. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Uveal Melanoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Uveal Melanoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Uveal Melanoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/uveal-melanoma",
      "keywords": [
        "Uveal Melanoma treatment",
        "Uveal Melanoma specialists",
        "Uveal Melanoma clinical trials",
        "Uveal Melanoma second opinion",
        "Uveal Melanoma prognosis",
        "Uveal Melanoma NCI cancer centers",
        "rare cancer care Uveal Melanoma"
      ]
    }
  },
  {
    "slug": "fibrolamellar-hepatocellular-carcinoma-fl-hcc",
    "aliases": [
      "fibrolamellar-hepatocellular-carcinoma",
      "fl-hcc",
      "fibrolamellar-carcinoma"
    ],
    "path": "/rare-cancers/fibrolamellar-hepatocellular-carcinoma-fl-hcc",
    "name": "Fibrolamellar Hepatocellular Carcinoma (FL-HCC)",
    "shortName": "Fibrolamellar Hepatocellular Carcinoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Liver Cancer",
    "description": "A distinct liver cancer of adolescents and young adults that arises in an otherwise healthy, non-cirrhotic liver with no hepatitis or alcohol history. It is defined by the DNAJB1-PRKACA fusion. Because patients are young and well, the diagnosis is frequently delayed.",
    "symptoms": [
      "Vague right upper abdominal pain or fullness",
      "A palpable abdominal mass",
      "Weight loss, nausea and fatigue",
      "Rarely jaundice, unlike conventional liver cancer",
      "Alpha-fetoprotein is characteristically normal, which can falsely reassure"
    ],
    "treatmentOptions": [
      "Complete surgical resection with lymphadenectomy - the only curative treatment",
      "Repeat resection or metastasectomy for recurrence, which is often worthwhile",
      "Liver transplantation in highly selected cases",
      "Systemic therapy in unresectable disease - limited standard options, trials preferred",
      "Locoregional therapy for liver-dominant disease"
    ],
    "prognosis": "Better than conventional hepatocellular carcinoma when completely resected, with five-year survival around 60-70%. Recurrence is common but often slow and amenable to further surgery. Lymph node involvement and unresectability are the main adverse factors.",
    "researchStatus": "Direct targeting of the DNAJB1-PRKACA fusion kinase, along with mTOR and immune approaches, is the central research effort; the Fibrolamellar Cancer Foundation maintains a patient registry and tissue bank.",
    "molecularMarkers": [
      "DNAJB1-PRKACA fusion (near-universal)",
      "CD68 and CK7 co-expression by IHC"
    ],
    "whoItAffects": "Adolescents and young adults, median around 25; no cirrhosis, equal sex distribution",
    "incidence": "Approximately 0.02 per 100,000 per year",
    "redFlags": [
      "Liver mass in a young patient with a normal AFP and no cirrhosis - test for the fusion"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Milind Javle",
        "title": "Medical Oncologist",
        "institution": "MD Anderson",
        "city": "Houston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Hepatobiliary Oncology",
          "FL-HCC",
          "Targeted Therapy"
        ]
      },
      {
        "name": "Dr. Pierce Chow",
        "title": "Hepatobiliary Surgeon",
        "institution": "NCCS/NHCS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 25,
        "specialties": [
          "Hepatobiliary Surgery",
          "FL-HCC",
          "Liver Tumor Board"
        ]
      },
      {
        "name": "Prof. Philip Johnson",
        "title": "Hepatologist",
        "institution": "University College London",
        "city": "London",
        "region": "Europe",
        "country": "United Kingdom",
        "experienceYears": 30,
        "specialties": [
          "Hepatology",
          "FL-HCC Registry",
          "Management Guidelines"
        ]
      },
      {
        "name": "Dr. Shripad Banavali",
        "title": "Medical Oncologist",
        "institution": "Tata Memorial",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Medical Oncology",
          "Rare Liver Tumors",
          "FL-HCC"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Fibrolamellar Hepatocellular Carcinoma (FL-HCC) and how is it diagnosed?",
        "answer": "A distinct liver cancer of adolescents and young adults that arises in an otherwise healthy, non-cirrhotic liver with no hepatitis or alcohol history. It is defined by the DNAJB1-PRKACA fusion. Because patients are young and well, the diagnosis is frequently delayed. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Fibrolamellar Hepatocellular Carcinoma (FL-HCC)?",
        "answer": "Standard first-line protocols include: Complete surgical resection with lymphadenectomy - the only curative treatment, Repeat resection or metastasectomy for recurrence, which is often worthwhile, Liver transplantation in highly selected cases. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Fibrolamellar Hepatocellular Carcinoma (FL-HCC)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: DNAJB1-PRKACA fusion (near-universal), CD68 and CK7 co-expression by IHC. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Liver mass in a young patient with a normal AFP and no cirrhosis - test for the fusion. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Direct targeting of the DNAJB1-PRKACA fusion kinase, along with mTOR and immune approaches, is the central research effort; the Fibrolamellar Cancer Foundation maintains a patient registry and tissue bank. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Fibrolamellar Hepatocellular Carcinoma (FL-HCC) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Fibrolamellar Hepatocellular Carcinoma (FL-HCC) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Fibrolamellar Hepatocellular Carcinoma (FL-HCC). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/fibrolamellar-hepatocellular-carcinoma-fl-hcc",
      "keywords": [
        "Fibrolamellar Hepatocellular Carcinoma (FL-HCC) treatment",
        "Fibrolamellar Hepatocellular Carcinoma (FL-HCC) specialists",
        "Fibrolamellar Hepatocellular Carcinoma (FL-HCC) clinical trials",
        "Fibrolamellar Hepatocellular Carcinoma (FL-HCC) second opinion",
        "Fibrolamellar Hepatocellular Carcinoma (FL-HCC) prognosis",
        "Fibrolamellar Hepatocellular Carcinoma (FL-HCC) NCI cancer centers",
        "rare cancer care Fibrolamellar Hepatocellular Carcinoma"
      ]
    }
  },
  {
    "slug": "adenoid-cystic-carcinoma-acc",
    "aliases": [
      "adenoid-cystic-carcinoma",
      "adcc",
      "salivary-acc"
    ],
    "path": "/rare-cancers/adenoid-cystic-carcinoma-acc",
    "name": "Adenoid Cystic Carcinoma (ACC)",
    "shortName": "Adenoid Cystic Carcinoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Salivary Gland Cancer",
    "description": "A slow-growing salivary gland cancer notorious for spreading along nerves and for recurring or metastasising many years after apparently successful treatment. It also arises in the lacrimal gland, breast, trachea and other secretory sites. Lung metastases can remain stable for years.",
    "symptoms": [
      "A slowly enlarging, often painless lump in the parotid, submandibular or palate region",
      "Persistent facial numbness, tingling or weakness - a hallmark of perineural spread",
      "Pain out of proportion to the size of the lump",
      "Nasal obstruction or hoarseness depending on the site",
      "Difficulty swallowing with pharyngeal involvement"
    ],
    "treatmentOptions": [
      "Wide surgical resection with nerve assessment",
      "Postoperative radiotherapy, including neutron, proton or carbon ion where available",
      "Radiotherapy covering named nerve pathways to the skull base",
      "Prolonged active surveillance of indolent lung metastases",
      "Lenvatinib, axitinib and other antiangiogenic TKIs in progressive disease"
    ],
    "prognosis": "A long disease. Five-year survival is relatively good at around 70-80%, but fifteen- and twenty-year survival fall substantially because of late recurrence. Solid histological subtype and perineural invasion predict worse outcome. Surveillance must extend for decades.",
    "researchStatus": "MYB/MYBL1-NFIB fusion targeting, NOTCH1 inhibitors for the NOTCH-activated aggressive subset, and antiangiogenic combinations are under study.",
    "molecularMarkers": [
      "MYB-NFIB fusion, t(6;9)",
      "MYBL1-NFIB - variant",
      "NOTCH1 activating mutations"
    ],
    "whoItAffects": "Adults, median 50-60; slight female predominance",
    "incidence": "Approximately 3-4.5 per million per year",
    "redFlags": [
      "Facial nerve weakness with a salivary mass - assume malignancy until proven otherwise"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Alan L. Ho",
        "title": "Medical Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 15,
        "specialties": [
          "Head & Neck Oncology",
          "Adenoid Cystic Carcinoma",
          "Targeted Agents"
        ]
      },
      {
        "name": "Dr. Gopal Iyer",
        "title": "Head & Neck Surgeon",
        "institution": "SingHealth",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Head & Neck Surgery",
          "Salivary Gland Tumors",
          "Molecular Targets"
        ]
      },
      {
        "name": "Prof. Laura V. Locati",
        "title": "Medical Oncologist",
        "institution": "Istituto Nazionale Tumori",
        "city": "Milan",
        "region": "Europe",
        "country": "Italy",
        "experienceYears": 20,
        "specialties": [
          "Salivary Gland Cancers",
          "Adenoid Cystic Carcinoma",
          "European Guidelines"
        ]
      },
      {
        "name": "Dr. Pankaj Chaturvedi",
        "title": "Head & Neck Surgical Oncologist",
        "institution": "Tata Memorial",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 25,
        "specialties": [
          "Head & Neck Surgery",
          "Adenoid Cystic Carcinoma",
          "Postoperative Radiotherapy"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Adenoid Cystic Carcinoma (ACC) and how is it diagnosed?",
        "answer": "A slow-growing salivary gland cancer notorious for spreading along nerves and for recurring or metastasising many years after apparently successful treatment. It also arises in the lacrimal gland, breast, trachea and other secretory sites. Lung metastases can remain stable for years. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Adenoid Cystic Carcinoma (ACC)?",
        "answer": "Standard first-line protocols include: Wide surgical resection with nerve assessment, Postoperative radiotherapy, including neutron, proton or carbon ion where available, Radiotherapy covering named nerve pathways to the skull base. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Adenoid Cystic Carcinoma (ACC)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: MYB-NFIB fusion, t(6;9), MYBL1-NFIB - variant, NOTCH1 activating mutations. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Facial nerve weakness with a salivary mass - assume malignancy until proven otherwise. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "MYB/MYBL1-NFIB fusion targeting, NOTCH1 inhibitors for the NOTCH-activated aggressive subset, and antiangiogenic combinations are under study. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Adenoid Cystic Carcinoma (ACC) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Adenoid Cystic Carcinoma (ACC) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Adenoid Cystic Carcinoma (ACC). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/adenoid-cystic-carcinoma-acc",
      "keywords": [
        "Adenoid Cystic Carcinoma (ACC) treatment",
        "Adenoid Cystic Carcinoma (ACC) specialists",
        "Adenoid Cystic Carcinoma (ACC) clinical trials",
        "Adenoid Cystic Carcinoma (ACC) second opinion",
        "Adenoid Cystic Carcinoma (ACC) prognosis",
        "Adenoid Cystic Carcinoma (ACC) NCI cancer centers",
        "rare cancer care Adenoid Cystic Carcinoma"
      ]
    }
  },
  {
    "slug": "esthesioneuroblastoma",
    "aliases": [
      "esthesioneuroblastoma",
      "olfactory-neuroblastoma"
    ],
    "path": "/rare-cancers/esthesioneuroblastoma",
    "name": "Esthesioneuroblastoma",
    "shortName": "Esthesioneuroblastoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Nasal Cavity Cancer",
    "description": "Also called olfactory neuroblastoma, this tumour arises from the olfactory neuroepithelium high in the nasal cavity, at the roof of the nose beneath the skull base. Because its early symptoms mimic ordinary sinus disease, it is often diagnosed only after it has eroded upward toward the brain or into the orbit.",
    "symptoms": [
      "One-sided nasal blockage that does not resolve",
      "Recurrent nosebleeds from the same side",
      "Loss of the sense of smell",
      "Eye symptoms - proptosis, double vision, tearing - with orbital extension",
      "Headache and, in advanced disease, altered mental state"
    ],
    "treatmentOptions": [
      "Endoscopic or open craniofacial resection with skull-base reconstruction",
      "Postoperative radiotherapy - standard for all but the earliest tumours",
      "Neoadjuvant or adjuvant chemotherapy for advanced Kadish C/D disease",
      "Neck dissection or elective neck irradiation for nodal disease",
      "Long-term surveillance imaging - late recurrence is well described"
    ],
    "prognosis": "Comparatively favourable for a skull-base malignancy. Five-year survival is around 70-80% overall, driven mainly by Kadish stage and Hyams histological grade; nodal involvement roughly halves survival.",
    "researchStatus": "Molecular subtyping is redefining risk groups, and somatostatin-receptor imaging and peptide receptor radionuclide therapy are being explored in receptor-positive tumours.",
    "molecularMarkers": [
      "No single defining lesion",
      "IDH2 R172 in a subset",
      "Somatostatin receptor 2 expression"
    ],
    "whoItAffects": "Adults, bimodal peaks in the 20s and 50s-60s",
    "incidence": "Approximately 0.4 per million per year",
    "redFlags": [
      "Unilateral nasal obstruction with anosmia - image the skull base"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Alexander G. Chiu",
        "title": "Head & Neck Surgeon",
        "institution": "Mayo Clinic",
        "city": "Phoenix",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Head & Neck Surgery",
          "Esthesioneuroblastoma",
          "Endoscopic Resection"
        ]
      },
      {
        "name": "Dr. Ian Loh",
        "title": "ENT Surgeon",
        "institution": "SGH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "ENT Surgery",
          "Sinonasal Tumors",
          "Minimally Invasive Surgery"
        ]
      },
      {
        "name": "Prof. Daniele Marchioni",
        "title": "ENT Surgeon",
        "institution": "University of Verona",
        "city": "Verona",
        "region": "Europe",
        "country": "Italy",
        "experienceYears": 20,
        "specialties": [
          "ENT Surgery",
          "Esthesioneuroblastoma",
          "Endoscopic Endonasal Approach"
        ]
      },
      {
        "name": "Dr. G. K. Rath",
        "title": "Radiation Oncologist",
        "institution": "AIIMS (former)",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 40,
        "specialties": [
          "Radiation Oncology",
          "Esthesioneuroblastoma",
          "IMRT"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Esthesioneuroblastoma and how is it diagnosed?",
        "answer": "Also called olfactory neuroblastoma, this tumour arises from the olfactory neuroepithelium high in the nasal cavity, at the roof of the nose beneath the skull base. Because its early symptoms mimic ordinary sinus disease, it is often diagnosed only after it has eroded upward toward the brain or into the orbit. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Esthesioneuroblastoma?",
        "answer": "Standard first-line protocols include: Endoscopic or open craniofacial resection with skull-base reconstruction, Postoperative radiotherapy - standard for all but the earliest tumours, Neoadjuvant or adjuvant chemotherapy for advanced Kadish C/D disease. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Esthesioneuroblastoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: No single defining lesion, IDH2 R172 in a subset, Somatostatin receptor 2 expression. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Unilateral nasal obstruction with anosmia - image the skull base. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Molecular subtyping is redefining risk groups, and somatostatin-receptor imaging and peptide receptor radionuclide therapy are being explored in receptor-positive tumours. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Esthesioneuroblastoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Esthesioneuroblastoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Esthesioneuroblastoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/esthesioneuroblastoma",
      "keywords": [
        "Esthesioneuroblastoma treatment",
        "Esthesioneuroblastoma specialists",
        "Esthesioneuroblastoma clinical trials",
        "Esthesioneuroblastoma second opinion",
        "Esthesioneuroblastoma prognosis",
        "Esthesioneuroblastoma NCI cancer centers",
        "rare cancer care Esthesioneuroblastoma"
      ]
    }
  },
  {
    "slug": "appendiceal-carcinoma",
    "aliases": [
      "appendiceal-carcinoma",
      "appendix-cancer"
    ],
    "path": "/rare-cancers/appendiceal-carcinoma",
    "name": "Appendiceal Carcinoma",
    "shortName": "Appendiceal Carcinoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "GI Cancer",
    "description": "A group of tumours of the appendix ranging from low-grade mucinous neoplasms, which spread by filling the abdomen with mucin (pseudomyxoma peritonei), to high-grade adenocarcinomas that behave like aggressive colorectal cancer. Most are found incidentally after an appendicectomy for suspected appendicitis.",
    "symptoms": [
      "Symptoms of acute appendicitis - right lower abdominal pain",
      "Progressive abdominal distension and increasing waist size",
      "A new hernia, or an ovarian mass in women",
      "Change in bowel habit",
      "Frequently no symptoms at all before surgery"
    ],
    "treatmentOptions": [
      "Appendicectomy for small, low-grade, completely excised tumours",
      "Right hemicolectomy for high-grade or larger tumours",
      "Cytoreductive surgery with hyperthermic intraperitoneal chemotherapy (HIPEC) for peritoneal disease",
      "Systemic chemotherapy (FOLFOX/FOLFIRI-type) for high-grade or unresectable disease",
      "Somatostatin analogues for appendiceal neuroendocrine tumours"
    ],
    "prognosis": "Strongly grade-dependent. Low-grade mucinous disease treated with complete cytoreduction and HIPEC has five-year survival above 80%, and long-term cure is achievable. High-grade adenocarcinoma with signet-ring cells has a considerably worse outlook.",
    "researchStatus": "Randomised evidence on HIPEC continues to evolve; molecular characterisation is separating appendiceal from colorectal biology and guiding trial design.",
    "molecularMarkers": [
      "KRAS",
      "GNAS - characteristic of low-grade mucinous tumours",
      "TP53 in high-grade disease"
    ],
    "whoItAffects": "Adults, median 50-60; slight female predominance in mucinous types",
    "incidence": "Approximately 1-2 per million per year, though rising with better recognition",
    "redFlags": [
      "Refer all appendiceal mucinous neoplasms to a peritoneal surface malignancy centre"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Muhammad M. Sardi",
        "title": "Surgical Oncologist",
        "institution": "Mercy Medical Center",
        "city": "Baltimore",
        "region": "USA",
        "country": "USA",
        "experienceYears": 30,
        "specialties": [
          "Surgical Oncology",
          "Appendiceal Cancer",
          "CRS+HIPEC"
        ]
      },
      {
        "name": "Dr. Tan Ker Kan",
        "title": "Surgical Oncologist",
        "institution": "NUH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Surgical Oncology",
          "Appendiceal Tumors",
          "CRS+HIPEC"
        ]
      },
      {
        "name": "Prof. Norman Carr",
        "title": "Gastrointestinal Oncologist",
        "institution": "Basingstoke Hospital",
        "city": "Basingstoke",
        "region": "Europe",
        "country": "United Kingdom",
        "experienceYears": 25,
        "specialties": [
          "GI Oncology",
          "Peritoneal Malignancy",
          "Appendix Tumors"
        ]
      },
      {
        "name": "Dr. Anil Heroor",
        "title": "Surgical Oncologist",
        "institution": "Private Practice",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Surgical Oncology",
          "HIPEC",
          "Appendiceal Mucinous Carcinoma"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Appendiceal Carcinoma and how is it diagnosed?",
        "answer": "A group of tumours of the appendix ranging from low-grade mucinous neoplasms, which spread by filling the abdomen with mucin (pseudomyxoma peritonei), to high-grade adenocarcinomas that behave like aggressive colorectal cancer. Most are found incidentally after an appendicectomy for suspected appendicitis. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Appendiceal Carcinoma?",
        "answer": "Standard first-line protocols include: Appendicectomy for small, low-grade, completely excised tumours, Right hemicolectomy for high-grade or larger tumours, Cytoreductive surgery with hyperthermic intraperitoneal chemotherapy (HIPEC) for peritoneal disease. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Appendiceal Carcinoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: KRAS, GNAS - characteristic of low-grade mucinous tumours, TP53 in high-grade disease. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Refer all appendiceal mucinous neoplasms to a peritoneal surface malignancy centre. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Randomised evidence on HIPEC continues to evolve; molecular characterisation is separating appendiceal from colorectal biology and guiding trial design. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Appendiceal Carcinoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Appendiceal Carcinoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Appendiceal Carcinoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/appendiceal-carcinoma",
      "keywords": [
        "Appendiceal Carcinoma treatment",
        "Appendiceal Carcinoma specialists",
        "Appendiceal Carcinoma clinical trials",
        "Appendiceal Carcinoma second opinion",
        "Appendiceal Carcinoma prognosis",
        "Appendiceal Carcinoma NCI cancer centers",
        "rare cancer care Appendiceal Carcinoma"
      ]
    }
  },
  {
    "slug": "angioimmunoblastic-t-cell-lymphoma-aitl",
    "aliases": [
      "angioimmunoblastic-t-cell-lymphoma",
      "aitl"
    ],
    "path": "/rare-cancers/angioimmunoblastic-t-cell-lymphoma-aitl",
    "name": "Angioimmunoblastic T-cell Lymphoma (AITL)",
    "shortName": "Angioimmunoblastic T-cell Lymphoma",
    "category": "very-rare",
    "categoryLabel": "Very Rare Malignancy (1\u20135 cases per 1,000,000 / year)",
    "type": "Lymphoma",
    "description": "A peripheral T-cell lymphoma arising from follicular helper T cells. It behaves as much like an immune disorder as a cancer, with widespread lymph node enlargement, rashes, autoimmune phenomena and profound systemic symptoms. Expanded EBV-positive B cells within the tumour are characteristic and can complicate diagnosis.",
    "symptoms": [
      "Generalised lymph node enlargement developing over weeks",
      "High fevers, drenching night sweats and weight loss",
      "Skin rash, often itchy and widespread",
      "Enlarged liver and spleen",
      "Autoimmune haemolytic anaemia, arthralgia and polyclonal raised immunoglobulins"
    ],
    "treatmentOptions": [
      "CHOP or CHOEP chemotherapy as first-line therapy",
      "Autologous stem cell transplant consolidation in first remission for fit patients",
      "Epigenetic agents - romidepsin, azacitidine, belinostat - particularly given TET2/DNMT3A lesions",
      "Pralatrexate or single-agent options in relapse",
      "Allogeneic transplant in selected relapsed patients"
    ],
    "prognosis": "Guarded. Five-year overall survival is roughly 30-35% with conventional chemotherapy. Younger patients who reach autologous transplant in first remission do better; the International Prognostic Index and the AITL-specific scores guide expectations.",
    "researchStatus": "Hypomethylating agents combined with CHOP show promise given the recurrent epigenetic mutations, and IDH2-mutant disease is a candidate for IDH2 inhibition. Duvelisib and JAK inhibitors are in trials.",
    "molecularMarkers": [
      "TET2",
      "DNMT3A",
      "IDH2 R172",
      "RHOA G17V",
      "TFH phenotype - CD10, BCL6, CXCL13, PD-1, ICOS"
    ],
    "whoItAffects": "Older adults, median around 65; slight male predominance",
    "incidence": "Approximately 0.05 per 100,000 per year",
    "redFlags": [],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Steven M. Horwitz",
        "title": "Medical Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "T-cell Lymphomas",
          "AITL",
          "Novel Therapies"
        ]
      },
      {
        "name": "Dr. Francesca Lim",
        "title": "Hematologist",
        "institution": "SGH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Hematology",
          "AITL",
          "Combined Chemo-immunotherapy"
        ]
      },
      {
        "name": "Prof. Ulrich J\u00e4ger",
        "title": "Hematologist",
        "institution": "Medical University of Vienna",
        "city": "Vienna",
        "region": "Europe",
        "country": "Austria",
        "experienceYears": 30,
        "specialties": [
          "Hematology",
          "AITL Trials",
          "Treatment Guidelines"
        ]
      },
      {
        "name": "Dr. Reena Nair",
        "title": "Hematologist",
        "institution": "TMC",
        "city": "Kolkata",
        "region": "India",
        "country": "India",
        "experienceYears": 25,
        "specialties": [
          "Hematology",
          "AITL",
          "Novel Agents"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Angioimmunoblastic T-cell Lymphoma (AITL) and how is it diagnosed?",
        "answer": "A peripheral T-cell lymphoma arising from follicular helper T cells. It behaves as much like an immune disorder as a cancer, with widespread lymph node enlargement, rashes, autoimmune phenomena and profound systemic symptoms. Expanded EBV-positive B cells within the tumour are characteristic and can complicate diagnosis. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Angioimmunoblastic T-cell Lymphoma (AITL)?",
        "answer": "Standard first-line protocols include: CHOP or CHOEP chemotherapy as first-line therapy, Autologous stem cell transplant consolidation in first remission for fit patients, Epigenetic agents - romidepsin, azacitidine, belinostat - particularly given TET2/DNMT3A lesions. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Angioimmunoblastic T-cell Lymphoma (AITL)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: TET2, DNMT3A, IDH2 R172, RHOA G17V, TFH phenotype - CD10, BCL6, CXCL13, PD-1, ICOS. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Hypomethylating agents combined with CHOP show promise given the recurrent epigenetic mutations, and IDH2-mutant disease is a candidate for IDH2 inhibition. Duvelisib and JAK inhibitors are in trials. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Angioimmunoblastic T-cell Lymphoma (AITL) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Angioimmunoblastic T-cell Lymphoma (AITL) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Angioimmunoblastic T-cell Lymphoma (AITL). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/angioimmunoblastic-t-cell-lymphoma-aitl",
      "keywords": [
        "Angioimmunoblastic T-cell Lymphoma (AITL) treatment",
        "Angioimmunoblastic T-cell Lymphoma (AITL) specialists",
        "Angioimmunoblastic T-cell Lymphoma (AITL) clinical trials",
        "Angioimmunoblastic T-cell Lymphoma (AITL) second opinion",
        "Angioimmunoblastic T-cell Lymphoma (AITL) prognosis",
        "Angioimmunoblastic T-cell Lymphoma (AITL) NCI cancer centers",
        "rare cancer care Angioimmunoblastic T-cell Lymphoma"
      ]
    }
  },
  {
    "slug": "cholangiocarcinoma",
    "aliases": [
      "cholangiocarcinoma",
      "bile-duct-cancer"
    ],
    "path": "/rare-cancers/cholangiocarcinoma",
    "name": "Cholangiocarcinoma",
    "shortName": "Cholangiocarcinoma",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Bile Duct Cancer",
    "description": "Cancer of the bile ducts, divided by anatomy into intrahepatic (within the liver), perihilar (at the junction of the main ducts, also called a Klatskin tumour) and distal. The distinction matters enormously: it determines the operation, the imaging pathway and, increasingly, the targeted therapy options, since actionable mutations cluster in intrahepatic disease.",
    "symptoms": [
      "Yellowing of the skin and eyes, with dark urine and pale stools",
      "Persistent itching",
      "Right upper abdominal pain",
      "Unexplained weight loss and loss of appetite",
      "Fever with cholangitis if the duct becomes infected"
    ],
    "treatmentOptions": [
      "Surgical resection - the only curative option, often with major liver resection",
      "Liver transplantation for selected perihilar tumours after neoadjuvant chemoradiation",
      "Gemcitabine-cisplatin plus durvalumab or pembrolizumab as first-line systemic therapy",
      "FGFR inhibitors (pemigatinib, futibatinib) for FGFR2 fusion-positive disease",
      "Ivosidenib for IDH1-mutated disease",
      "Biliary drainage by stent or drain for obstruction, and photodynamic therapy in selected cases"
    ],
    "prognosis": "Overall poor because most patients present unresectable, with five-year survival under 20%. Completely resected node-negative disease does considerably better. Molecularly targeted therapy has extended survival meaningfully in the FGFR2 and IDH1 subgroups, which makes comprehensive genomic profiling essential at diagnosis.",
    "researchStatus": "Next-generation FGFR inhibitors that overcome resistance mutations, HER2-directed therapy, and adjuvant immunotherapy trials are the main areas of development.",
    "molecularMarkers": [
      "FGFR2 fusions (intrahepatic)",
      "IDH1/IDH2",
      "BAP1",
      "HER2 amplification (distal)",
      "KRAS",
      "MSI-H in a small subset"
    ],
    "whoItAffects": "Adults over 50; higher risk with primary sclerosing cholangitis, liver fluke infection, hepatolithiasis",
    "incidence": "Approximately 1-2 per 100,000 in Western countries, far higher in parts of Southeast Asia",
    "redFlags": [
      "Painless jaundice - image urgently and refer to a hepatobiliary centre"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Ghassan K. Abou-Alfa",
        "title": "Medical Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 25,
        "specialties": [
          "Hepatobiliary Oncology",
          "Cholangiocarcinoma",
          "FGFR/IDH Inhibitors"
        ]
      },
      {
        "name": "Dr. Joanne Ngeow",
        "title": "Medical Oncologist",
        "institution": "NCCS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Medical Oncology",
          "Biliary Tract Cancers",
          "Precision Oncology"
        ]
      },
      {
        "name": "Prof. Juan Valle",
        "title": "Medical Oncologist",
        "institution": "The Christie",
        "city": "Manchester",
        "region": "Europe",
        "country": "United Kingdom",
        "experienceYears": 20,
        "specialties": [
          "Medical Oncology",
          "Cholangiocarcinoma",
          "ABC Trials"
        ]
      },
      {
        "name": "Dr. Amit Jain",
        "title": "HPB Surgeon",
        "institution": "ILBS",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "HPB Surgery",
          "Cholangiocarcinoma",
          "Curative Resections"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Cholangiocarcinoma and how is it diagnosed?",
        "answer": "Cancer of the bile ducts, divided by anatomy into intrahepatic (within the liver), perihilar (at the junction of the main ducts, also called a Klatskin tumour) and distal. The distinction matters enormously: it determines the operation, the imaging pathway and, increasingly, the targeted therapy options, since actionable mutations cluster in intrahepatic disease. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Cholangiocarcinoma?",
        "answer": "Standard first-line protocols include: Surgical resection - the only curative option, often with major liver resection, Liver transplantation for selected perihilar tumours after neoadjuvant chemoradiation, Gemcitabine-cisplatin plus durvalumab or pembrolizumab as first-line systemic therapy. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Cholangiocarcinoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: FGFR2 fusions (intrahepatic), IDH1/IDH2, BAP1, HER2 amplification (distal), KRAS, MSI-H in a small subset. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Painless jaundice - image urgently and refer to a hepatobiliary centre. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Next-generation FGFR inhibitors that overcome resistance mutations, HER2-directed therapy, and adjuvant immunotherapy trials are the main areas of development. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Cholangiocarcinoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Cholangiocarcinoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Cholangiocarcinoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/cholangiocarcinoma",
      "keywords": [
        "Cholangiocarcinoma treatment",
        "Cholangiocarcinoma specialists",
        "Cholangiocarcinoma clinical trials",
        "Cholangiocarcinoma second opinion",
        "Cholangiocarcinoma prognosis",
        "Cholangiocarcinoma NCI cancer centers",
        "rare cancer care Cholangiocarcinoma"
      ]
    }
  },
  {
    "slug": "gestational-trophoblastic-neoplasia",
    "aliases": [
      "gestational-trophoblastic-neoplasia",
      "gtn",
      "choriocarcinoma"
    ],
    "path": "/rare-cancers/gestational-trophoblastic-neoplasia",
    "name": "Gestational Trophoblastic Neoplasia",
    "shortName": "Gestational Trophoblastic Neoplasia",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Pregnancy-related",
    "description": "A group of tumours arising from placental tissue after any pregnancy, including complete and partial hydatidiform mole, invasive mole, choriocarcinoma, placental site trophoblastic tumour and epithelioid trophoblastic tumour. It is one of the few metastatic solid cancers that is reliably curable with chemotherapy, and hCG provides an exceptionally accurate tumour marker for both diagnosis and monitoring.",
    "symptoms": [
      "Vaginal bleeding after a pregnancy, miscarriage or molar pregnancy",
      "A uterus larger than expected for dates",
      "hCG that fails to fall, or rises, after pregnancy ends",
      "Severe nausea and vomiting, or early pre-eclampsia in molar pregnancy",
      "Cough or breathlessness from lung metastases; headache or seizures from brain spread"
    ],
    "treatmentOptions": [
      "Suction evacuation of the uterus for molar pregnancy",
      "Single-agent methotrexate or actinomycin-D for low-risk disease (FIGO score 0-6)",
      "EMA-CO combination chemotherapy for high-risk disease (score 7 or above)",
      "Surgery for placental site and epithelioid trophoblastic tumours, which are chemo-resistant",
      "Structured hCG surveillance after treatment, with contraception during follow-up"
    ],
    "prognosis": "Excellent, and among the best of any metastatic malignancy. Low-risk disease is cured in essentially all patients, and high-risk disease has cure rates above 90% with combination chemotherapy. Fertility is usually preserved. Ultra-high-risk and placental site tumours are the exceptions.",
    "researchStatus": "Immune checkpoint inhibitors, notably pembrolizumab and avelumab, have shown activity in chemoresistant disease and are being incorporated into salvage protocols - an important development for the small refractory group.",
    "molecularMarkers": [
      "Serum beta-hCG - the defining marker",
      "p57 immunostaining to distinguish complete from partial mole"
    ],
    "whoItAffects": "People of reproductive age after any pregnancy; risk rises at the extremes of maternal age",
    "incidence": "Roughly 1 in 1,000 pregnancies for molar pregnancy; higher in parts of Asia",
    "redFlags": [
      "Any hCG plateau or rise after pregnancy - refer to a trophoblastic disease centre"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Neil Horowitz",
        "title": "Director of Clinical Research in Gynecologic Oncology",
        "institution": "Dana-Farber Cancer Institute",
        "city": "Boston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Gynecologic Oncology",
          "GTN",
          "Trophoblastic Disease Center"
        ]
      },
      {
        "name": "Dr. Ann Tan",
        "title": "Senior Consultant OBGYN",
        "institution": "Mount Elizabeth",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 30,
        "specialties": [
          "OBGYN",
          "Molar Pregnancies",
          "GTN"
        ]
      },
      {
        "name": "Prof. Michael J. Seckl",
        "title": "Medical Oncologist",
        "institution": "Charing Cross Trophoblast Center",
        "city": "London",
        "region": "Europe",
        "country": "United Kingdom",
        "experienceYears": 25,
        "specialties": [
          "Medical Oncology",
          "GTN",
          "Clinical Trials"
        ]
      },
      {
        "name": "Dr. Niranjan Chavan",
        "title": "Gynecologic Oncologist",
        "institution": "King Edward Memorial Hospital",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Gynecologic Oncology",
          "High-risk GTN",
          "Combination Chemotherapy"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Gestational Trophoblastic Neoplasia and how is it diagnosed?",
        "answer": "A group of tumours arising from placental tissue after any pregnancy, including complete and partial hydatidiform mole, invasive mole, choriocarcinoma, placental site trophoblastic tumour and epithelioid trophoblastic tumour. It is one of the few metastatic solid cancers that is reliably curable with chemotherapy, and hCG provides an exceptionally accurate tumour marker for both diagnosis and monitoring. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Gestational Trophoblastic Neoplasia?",
        "answer": "Standard first-line protocols include: Suction evacuation of the uterus for molar pregnancy, Single-agent methotrexate or actinomycin-D for low-risk disease (FIGO score 0-6), EMA-CO combination chemotherapy for high-risk disease (score 7 or above). Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Gestational Trophoblastic Neoplasia?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: Serum beta-hCG - the defining marker, p57 immunostaining to distinguish complete from partial mole. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Any hCG plateau or rise after pregnancy - refer to a trophoblastic disease centre. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Immune checkpoint inhibitors, notably pembrolizumab and avelumab, have shown activity in chemoresistant disease and are being incorporated into salvage protocols - an important development for the small refractory group. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Gestational Trophoblastic Neoplasia through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Gestational Trophoblastic Neoplasia Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Gestational Trophoblastic Neoplasia. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/gestational-trophoblastic-neoplasia",
      "keywords": [
        "Gestational Trophoblastic Neoplasia treatment",
        "Gestational Trophoblastic Neoplasia specialists",
        "Gestational Trophoblastic Neoplasia clinical trials",
        "Gestational Trophoblastic Neoplasia second opinion",
        "Gestational Trophoblastic Neoplasia prognosis",
        "Gestational Trophoblastic Neoplasia NCI cancer centers",
        "rare cancer care Gestational Trophoblastic Neoplasia"
      ]
    }
  },
  {
    "slug": "thymic-carcinoma",
    "aliases": [
      "thymic-carcinoma",
      "thymus-cancer"
    ],
    "path": "/rare-cancers/thymic-carcinoma",
    "name": "Thymic Carcinoma",
    "shortName": "Thymic Carcinoma",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Mediastinal Tumor",
    "description": "A malignant tumour of the thymus gland in the front of the chest. It is biologically more aggressive than thymoma, invades locally and spreads more readily, and - unlike thymoma - is only rarely associated with myasthenia gravis or other autoimmune syndromes.",
    "symptoms": [
      "Chest pain or a feeling of pressure behind the breastbone",
      "Cough and breathlessness",
      "Facial and arm swelling with distended neck veins (superior vena cava obstruction)",
      "Hoarseness from recurrent laryngeal nerve involvement",
      "Often found incidentally on a chest scan performed for another reason"
    ],
    "treatmentOptions": [
      "Complete surgical resection where achievable - the strongest predictor of survival",
      "Postoperative radiotherapy for incomplete resection or advanced stage",
      "Carboplatin-paclitaxel as standard first-line chemotherapy",
      "Sunitinib or lenvatinib in relapsed disease",
      "Pembrolizumab in selected patients, with careful autoimmune toxicity monitoring"
    ],
    "prognosis": "Worse than thymoma. Five-year survival is roughly 30-50% overall, but considerably better for completely resected early-stage disease. Masaoka-Koga stage and completeness of resection are the dominant prognostic factors.",
    "researchStatus": "KIT-mutated cases may respond to imatinib or sunitinib; combination immunotherapy and antiangiogenic strategies are in trials, coordinated internationally through thymic malignancy interest groups.",
    "molecularMarkers": [
      "KIT mutations in about 10%",
      "CD5 and CD117 positivity",
      "GTF2I - characteristic of thymoma rather than carcinoma"
    ],
    "whoItAffects": "Adults, median 50-60; slight male predominance",
    "incidence": "Approximately 0.15-0.3 per 100,000 per year",
    "redFlags": [
      "Superior vena cava obstruction requires urgent assessment"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "RED_FLAGS_ONLY",
    "specialists": [
      {
        "name": "Dr. Giuseppe Giaccone",
        "title": "Medical Oncologist",
        "institution": "Georgetown University (formerly NCI)",
        "city": "Washington, DC",
        "region": "USA",
        "country": "USA",
        "experienceYears": 30,
        "specialties": [
          "Medical Oncology",
          "Thymic Malignancies",
          "Global Trials"
        ]
      },
      {
        "name": "Dr. Toh Han Chong",
        "title": "Medical Oncologist",
        "institution": "NCCS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 25,
        "specialties": [
          "Medical Oncology",
          "Thymic Carcinoma",
          "Targeted Agents"
        ]
      },
      {
        "name": "Prof. Luciano Mariani",
        "title": "Thoracic Surgeon",
        "institution": "Regina Elena Institute",
        "city": "Rome",
        "region": "Europe",
        "country": "Italy",
        "experienceYears": 20,
        "specialties": [
          "Thoracic Surgery",
          "Thymic Carcinoma",
          "Extensive Resections"
        ]
      },
      {
        "name": "Dr. Raja Sundaram",
        "title": "Surgical Oncologist",
        "institution": "Apollo Hospitals",
        "city": "Chennai",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Surgical Oncology",
          "Thymic Tumor Resection",
          "Thymoma Teams"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Thymic Carcinoma and how is it diagnosed?",
        "answer": "A malignant tumour of the thymus gland in the front of the chest. It is biologically more aggressive than thymoma, invades locally and spreads more readily, and - unlike thymoma - is only rarely associated with myasthenia gravis or other autoimmune syndromes. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Thymic Carcinoma?",
        "answer": "Standard first-line protocols include: Complete surgical resection where achievable - the strongest predictor of survival, Postoperative radiotherapy for incomplete resection or advanced stage, Carboplatin-paclitaxel as standard first-line chemotherapy. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Thymic Carcinoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: KIT mutations in about 10%, CD5 and CD117 positivity, GTF2I - characteristic of thymoma rather than carcinoma. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Superior vena cava obstruction requires urgent assessment. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "KIT-mutated cases may respond to imatinib or sunitinib; combination immunotherapy and antiangiogenic strategies are in trials, coordinated internationally through thymic malignancy interest groups. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Thymic Carcinoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Thymic Carcinoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Thymic Carcinoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/thymic-carcinoma",
      "keywords": [
        "Thymic Carcinoma treatment",
        "Thymic Carcinoma specialists",
        "Thymic Carcinoma clinical trials",
        "Thymic Carcinoma second opinion",
        "Thymic Carcinoma prognosis",
        "Thymic Carcinoma NCI cancer centers",
        "rare cancer care Thymic Carcinoma"
      ]
    }
  },
  {
    "slug": "gastrointestinal-stromal-tumor-gist",
    "aliases": [
      "gastrointestinal-stromal-tumor",
      "gist"
    ],
    "path": "/rare-cancers/gastrointestinal-stromal-tumor-gist",
    "name": "Gastrointestinal Stromal Tumor (GIST)",
    "shortName": "Gastrointestinal Stromal Tumor",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "GI Cancer",
    "description": "The commonest mesenchymal tumour of the digestive tract, arising from the interstitial cells of Cajal that pace intestinal contraction. GIST is the definitive success story of targeted therapy in solid tumours: identifying KIT as the driver turned a chemotherapy-resistant cancer into one controlled for years by oral tablets.",
    "symptoms": [
      "Vague abdominal discomfort or early fullness",
      "Gastrointestinal bleeding - black stools, vomiting blood, or iron-deficiency anaemia",
      "A palpable abdominal mass",
      "Nausea or obstruction with larger tumours",
      "Frequently found incidentally at endoscopy or on imaging"
    ],
    "treatmentOptions": [
      "Complete surgical resection without rupture; lymphadenectomy is not required",
      "Adjuvant imatinib for three years in high-risk resected disease",
      "Imatinib as first-line therapy for advanced disease, dose-adjusted by genotype",
      "Sunitinib, then regorafenib, then ripretinib in successive lines",
      "Avapritinib specifically for PDGFRA D842V-mutated disease, which is imatinib-resistant",
      "SDH testing in wild-type and paediatric GIST, with different management implications"
    ],
    "prognosis": "Generally good and greatly improved by targeted therapy. Completely resected low-risk tumours are usually cured. Advanced disease is now commonly controlled for several years; median survival on imatinib is around five years, and longer in favourable genotypes such as KIT exon 11.",
    "researchStatus": "Next-generation KIT inhibitors designed against specific resistance mutations, and combination strategies to prevent resistance emerging, are the leading directions. SDH-deficient GIST is being studied as a biologically separate disease.",
    "molecularMarkers": [
      "KIT exon 11 (most common)",
      "KIT exon 9",
      "PDGFRA including D842V",
      "SDHA/B/C/D deficiency",
      "BRAF, NF1 in wild-type disease"
    ],
    "whoItAffects": "Adults, median 60-65; SDH-deficient forms affect children and young adults",
    "incidence": "Approximately 10-15 per million per year",
    "redFlags": [
      "Genotype every GIST before starting therapy - it changes drug choice and dose"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. George D. Demetri",
        "title": "Medical Oncologist",
        "institution": "Dana-Farber",
        "city": "Boston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 30,
        "specialties": [
          "Medical Oncology",
          "GIST",
          "Imatinib Pioneer"
        ]
      },
      {
        "name": "Dr. Wong Seng Weng",
        "title": "Medical Oncologist",
        "institution": "Parkway Cancer Centre",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Medical Oncology",
          "GIST",
          "TKIs"
        ]
      },
      {
        "name": "Prof. Peter Reichardt",
        "title": "Oncologist",
        "institution": "HELIOS Clinic",
        "city": "Berlin",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 25,
        "specialties": [
          "Sarcoma Oncology",
          "GIST Studies",
          "EORTC"
        ]
      },
      {
        "name": "Dr. Rajiv Agarwal",
        "title": "Medical Oncologist",
        "institution": "Tata Memorial",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Medical Oncology",
          "GIST",
          "Imatinib"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Gastrointestinal Stromal Tumor (GIST) and how is it diagnosed?",
        "answer": "The commonest mesenchymal tumour of the digestive tract, arising from the interstitial cells of Cajal that pace intestinal contraction. GIST is the definitive success story of targeted therapy in solid tumours: identifying KIT as the driver turned a chemotherapy-resistant cancer into one controlled for years by oral tablets. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Gastrointestinal Stromal Tumor (GIST)?",
        "answer": "Standard first-line protocols include: Complete surgical resection without rupture; lymphadenectomy is not required, Adjuvant imatinib for three years in high-risk resected disease, Imatinib as first-line therapy for advanced disease, dose-adjusted by genotype. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Gastrointestinal Stromal Tumor (GIST)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: KIT exon 11 (most common), KIT exon 9, PDGFRA including D842V, SDHA/B/C/D deficiency, BRAF, NF1 in wild-type disease. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Genotype every GIST before starting therapy - it changes drug choice and dose. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Next-generation KIT inhibitors designed against specific resistance mutations, and combination strategies to prevent resistance emerging, are the leading directions. SDH-deficient GIST is being studied as a biologically separate disease. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Gastrointestinal Stromal Tumor (GIST) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Gastrointestinal Stromal Tumor (GIST) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Gastrointestinal Stromal Tumor (GIST). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/gastrointestinal-stromal-tumor-gist",
      "keywords": [
        "Gastrointestinal Stromal Tumor (GIST) treatment",
        "Gastrointestinal Stromal Tumor (GIST) specialists",
        "Gastrointestinal Stromal Tumor (GIST) clinical trials",
        "Gastrointestinal Stromal Tumor (GIST) second opinion",
        "Gastrointestinal Stromal Tumor (GIST) prognosis",
        "Gastrointestinal Stromal Tumor (GIST) NCI cancer centers",
        "rare cancer care Gastrointestinal Stromal Tumor"
      ]
    }
  },
  {
    "slug": "pancreatic-neuroendocrine-tumors-pnets",
    "aliases": [
      "pancreatic-neuroendocrine-tumors",
      "pnets",
      "pancreatic-net"
    ],
    "path": "/rare-cancers/pancreatic-neuroendocrine-tumors-pnets",
    "name": "Pancreatic Neuroendocrine Tumors (pNETs)",
    "shortName": "Pancreatic Neuroendocrine Tumors",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Pancreatic Cancer",
    "description": "Tumours of the hormone-producing islet cells of the pancreas. They are entirely distinct from pancreatic adenocarcinoma - typically far slower growing, with a very different treatment pathway and a much better outlook. Functional tumours secrete hormones (insulin, gastrin, glucagon, VIP) and cause characteristic syndromes; the majority are non-functional and present with mass effect.",
    "symptoms": [
      "Non-functional: abdominal pain, jaundice, or an incidental finding on imaging",
      "Insulinoma: confusion, sweating and collapse relieved by eating",
      "Gastrinoma: severe recurrent peptic ulcers and diarrhoea (Zollinger-Ellison syndrome)",
      "Glucagonoma: a migrating rash, new diabetes and weight loss",
      "VIPoma: profuse watery diarrhoea with low potassium"
    ],
    "treatmentOptions": [
      "Surgical resection or enucleation, curative for localised disease",
      "Somatostatin analogues (octreotide, lanreotide) for control of growth and hormone symptoms",
      "Peptide receptor radionuclide therapy with lutetium-177 dotatate for somatostatin-receptor-positive disease",
      "Everolimus or sunitinib as targeted systemic options",
      "Capecitabine-temozolomide chemotherapy, particularly effective in pNETs",
      "Liver-directed therapy - embolisation or ablation - for liver-dominant metastases",
      "Genetic testing for MEN1, VHL, NF1 and tuberous sclerosis where indicated"
    ],
    "prognosis": "Considerably better than pancreatic adenocarcinoma and highly dependent on grade. Well-differentiated G1/G2 tumours have five-year survival above 80-90% when localised, and multi-year survival is common even with liver metastases. Poorly differentiated neuroendocrine carcinoma is a separate, far more aggressive disease.",
    "researchStatus": "Sequencing and combining PRRT with other systemic agents, next-generation somatostatin-receptor radioligands, and immunotherapy in high-grade disease are the main research fronts.",
    "molecularMarkers": [
      "MEN1",
      "DAXX/ATRX",
      "mTOR pathway (TSC2, PTEN)",
      "Ki-67 index for grading",
      "Somatostatin receptor expression on Ga-68 DOTATATE PET"
    ],
    "whoItAffects": "Adults, median 50-60; earlier in hereditary syndromes such as MEN1 and VHL",
    "incidence": "Approximately 4-5 per million per year and rising with incidental detection",
    "redFlags": [
      "Do not manage as pancreatic adenocarcinoma - the pathway and prognosis differ entirely"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. James Yao",
        "title": "Medical Oncologist",
        "institution": "MD Anderson",
        "city": "Houston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Medical Oncology",
          "Neuroendocrine Tumors",
          "Everolimus/Sunitinib"
        ]
      },
      {
        "name": "Dr. Thng Choon Hua",
        "title": "Endocrinologist",
        "institution": "SGH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Endocrinology",
          "pNETs",
          "SSA Therapy/PRRT"
        ]
      },
      {
        "name": "Prof. Kjell \u00d6berg",
        "title": "Endocrine Oncologist",
        "institution": "Uppsala University",
        "city": "Uppsala",
        "region": "Europe",
        "country": "Sweden",
        "experienceYears": 40,
        "specialties": [
          "Endocrine Oncology",
          "NETs",
          "ENETS Guidelines"
        ]
      },
      {
        "name": "Dr. C. S. Pramesh",
        "title": "GI Surgical Oncologist, Director",
        "institution": "Tata Memorial",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 25,
        "specialties": [
          "GI Surgical Oncology",
          "Pancreas Surgery",
          "pNETs"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Pancreatic Neuroendocrine Tumors (pNETs) and how is it diagnosed?",
        "answer": "Tumours of the hormone-producing islet cells of the pancreas. They are entirely distinct from pancreatic adenocarcinoma - typically far slower growing, with a very different treatment pathway and a much better outlook. Functional tumours secrete hormones (insulin, gastrin, glucagon, VIP) and cause characteristic syndromes; the majority are non-functional and present with mass effect. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Pancreatic Neuroendocrine Tumors (pNETs)?",
        "answer": "Standard first-line protocols include: Surgical resection or enucleation, curative for localised disease, Somatostatin analogues (octreotide, lanreotide) for control of growth and hormone symptoms, Peptide receptor radionuclide therapy with lutetium-177 dotatate for somatostatin-receptor-positive disease. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Pancreatic Neuroendocrine Tumors (pNETs)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: MEN1, DAXX/ATRX, mTOR pathway (TSC2, PTEN), Ki-67 index for grading, Somatostatin receptor expression on Ga-68 DOTATATE PET. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Do not manage as pancreatic adenocarcinoma - the pathway and prognosis differ entirely. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Sequencing and combining PRRT with other systemic agents, next-generation somatostatin-receptor radioligands, and immunotherapy in high-grade disease are the main research fronts. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Pancreatic Neuroendocrine Tumors (pNETs) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Pancreatic Neuroendocrine Tumors (pNETs) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Pancreatic Neuroendocrine Tumors (pNETs). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/pancreatic-neuroendocrine-tumors-pnets",
      "keywords": [
        "Pancreatic Neuroendocrine Tumors (pNETs) treatment",
        "Pancreatic Neuroendocrine Tumors (pNETs) specialists",
        "Pancreatic Neuroendocrine Tumors (pNETs) clinical trials",
        "Pancreatic Neuroendocrine Tumors (pNETs) second opinion",
        "Pancreatic Neuroendocrine Tumors (pNETs) prognosis",
        "Pancreatic Neuroendocrine Tumors (pNETs) NCI cancer centers",
        "rare cancer care Pancreatic Neuroendocrine Tumors"
      ]
    }
  },
  {
    "slug": "penile-squamous-cell-carcinoma",
    "aliases": [
      "penile-squamous-cell-carcinoma",
      "penile-cancer"
    ],
    "path": "/rare-cancers/penile-squamous-cell-carcinoma",
    "name": "Penile Squamous Cell Carcinoma",
    "shortName": "Penile Squamous Cell Carcinoma",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Genitourinary",
    "description": "A cancer of the skin and tissues of the penis, most often squamous cell carcinoma. Around half of cases are related to human papillomavirus; the remainder are linked to chronic inflammation, phimosis, poor hygiene and smoking. It is far commoner in parts of South America, Africa and Asia than in high-income Western countries.",
    "symptoms": [
      "A non-healing sore, ulcer or wart-like growth on the glans or foreskin",
      "Bleeding or foul-smelling discharge",
      "A change in skin colour or thickening of the skin",
      "Difficulty retracting the foreskin",
      "Lumps in the groin from lymph node spread"
    ],
    "treatmentOptions": [
      "Organ-preserving surgery - glansectomy, wide local excision, laser or Mohs surgery",
      "Partial or total penectomy for larger or deeply invasive tumours",
      "Inguinal lymph node staging - dynamic sentinel node biopsy or lymphadenectomy",
      "Chemoradiotherapy or brachytherapy for selected organ preservation",
      "Cisplatin-based chemotherapy for advanced or node-positive disease"
    ],
    "prognosis": "Strongly determined by lymph node status. Node-negative disease has five-year survival above 85%, whereas pelvic nodal involvement drops this below 30%. Early accurate nodal staging is therefore the single most important management decision - delayed lymphadenectomy costs lives.",
    "researchStatus": "Immune checkpoint inhibitors in HPV-driven disease, and neoadjuvant chemotherapy before lymphadenectomy in bulky nodal disease, are the main trial directions; international collaboration is essential given the rarity.",
    "molecularMarkers": [
      "HPV 16/18 with p16 overexpression in about half",
      "TP53",
      "CDKN2A",
      "PIK3CA"
    ],
    "whoItAffects": "Men over 60, though HPV-associated cases occur younger; childhood circumcision is protective",
    "incidence": "Under 1 per 100,000 in Europe and North America; several times higher in parts of South America, Africa and South Asia",
    "redFlags": [
      "Any penile lesion not healing within four weeks needs biopsy, not further topical treatment"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Philippe E. Spiess",
        "title": "Urologic Oncologist",
        "institution": "Moffitt Cancer Center",
        "city": "Tampa",
        "region": "USA",
        "country": "USA",
        "experienceYears": 15,
        "specialties": [
          "Urologic Oncology",
          "Penile Cancer",
          "Nodal Dissection"
        ]
      },
      {
        "name": "Dr. Tan Teing Ee",
        "title": "Urologist",
        "institution": "SGH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Urology",
          "Penile Cancer",
          "Penectomy"
        ]
      },
      {
        "name": "Prof. Henk van der Poel",
        "title": "Urologic Oncologist",
        "institution": "Netherlands Cancer Institute",
        "city": "Amsterdam",
        "region": "Europe",
        "country": "Netherlands",
        "experienceYears": 25,
        "specialties": [
          "Urologic Oncology",
          "Penile Sparing Techniques",
          "Sentinel Node"
        ]
      },
      {
        "name": "Dr. Anup Kumar",
        "title": "Urologic Oncologist",
        "institution": "Safdarjung Hospital",
        "city": "New Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Urologic Oncology",
          "Penile Cancer Surgery",
          "Reconstructive Techniques"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Penile Squamous Cell Carcinoma and how is it diagnosed?",
        "answer": "A cancer of the skin and tissues of the penis, most often squamous cell carcinoma. Around half of cases are related to human papillomavirus; the remainder are linked to chronic inflammation, phimosis, poor hygiene and smoking. It is far commoner in parts of South America, Africa and Asia than in high-income Western countries. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Penile Squamous Cell Carcinoma?",
        "answer": "Standard first-line protocols include: Organ-preserving surgery - glansectomy, wide local excision, laser or Mohs surgery, Partial or total penectomy for larger or deeply invasive tumours, Inguinal lymph node staging - dynamic sentinel node biopsy or lymphadenectomy. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Penile Squamous Cell Carcinoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: HPV 16/18 with p16 overexpression in about half, TP53, CDKN2A, PIK3CA. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Any penile lesion not healing within four weeks needs biopsy, not further topical treatment. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Immune checkpoint inhibitors in HPV-driven disease, and neoadjuvant chemotherapy before lymphadenectomy in bulky nodal disease, are the main trial directions; international collaboration is essential given the rarity. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Penile Squamous Cell Carcinoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Penile Squamous Cell Carcinoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Penile Squamous Cell Carcinoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/penile-squamous-cell-carcinoma",
      "keywords": [
        "Penile Squamous Cell Carcinoma treatment",
        "Penile Squamous Cell Carcinoma specialists",
        "Penile Squamous Cell Carcinoma clinical trials",
        "Penile Squamous Cell Carcinoma second opinion",
        "Penile Squamous Cell Carcinoma prognosis",
        "Penile Squamous Cell Carcinoma NCI cancer centers",
        "rare cancer care Penile Squamous Cell Carcinoma"
      ]
    }
  },
  {
    "slug": "vulvar-cancer",
    "aliases": [
      "vulvar-cancer"
    ],
    "path": "/rare-cancers/vulvar-cancer",
    "name": "Vulvar Cancer",
    "shortName": "Vulvar Cancer",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Gynecologic",
    "description": "Cancer of the external female genitalia, most often squamous cell carcinoma. It arises by two distinct routes: an HPV-driven pathway affecting younger women, and a pathway from chronic lichen sclerosus and differentiated VIN in older women, which carries a worse prognosis. Diagnosis is frequently delayed because symptoms are attributed to thrush or menopausal change.",
    "symptoms": [
      "Persistent itching that does not respond to treatment",
      "A lump, ulcer or wart-like growth on the vulva",
      "A patch of skin that is white, red or darker than the surrounding skin",
      "Pain, burning or bleeding not related to periods",
      "Groin lumps from lymph node involvement"
    ],
    "treatmentOptions": [
      "Wide radical local excision with an adequate margin",
      "Sentinel lymph node biopsy for tumours under 4 cm with a clinically negative groin",
      "Inguinofemoral lymphadenectomy where sentinel biopsy is not appropriate",
      "Adjuvant radiotherapy for close margins or nodal involvement",
      "Definitive chemoradiotherapy for locally advanced disease to avoid exenteration",
      "Long-term dermatological management of lichen sclerosus to reduce recurrence"
    ],
    "prognosis": "Good when caught early. Five-year survival exceeds 85-90% for node-negative disease but falls to roughly 50% with inguinal node involvement and lower with multiple involved nodes. The dominant problem is presentation delay, not treatment failure.",
    "researchStatus": "Sentinel node techniques to reduce the substantial lymphoedema burden of full lymphadenectomy, immunotherapy in advanced HPV-driven disease, and HPV vaccination as primary prevention are the key developments.",
    "molecularMarkers": [
      "HPV 16 with p16 positivity in the younger cohort",
      "TP53 in the lichen sclerosus-associated pathway"
    ],
    "whoItAffects": "Predominantly women over 65, but rising in younger women through HPV",
    "incidence": "Approximately 2-3 per 100,000 women per year",
    "redFlags": [
      "Vulval itching lasting beyond a few weeks in a postmenopausal woman warrants examination and biopsy, not repeat antifungals"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Kathleen M. Schmeler",
        "title": "Gynecologic Oncologist",
        "institution": "MD Anderson",
        "city": "Houston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 15,
        "specialties": [
          "Gynecologic Oncology",
          "Vulvar Cancer",
          "Sentinel Node Dissection"
        ]
      },
      {
        "name": "Dr. See Tho Kai Yin",
        "title": "Senior Gynecologic Oncologist",
        "institution": "KKH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 30,
        "specialties": [
          "Gynecologic Oncology",
          "Vulvar Cancer",
          "Combined Modality"
        ]
      },
      {
        "name": "Prof. Lukas J. Bamberg",
        "title": "Gynecologic Oncologist",
        "institution": "Charit\u00e9 Berlin",
        "city": "Berlin",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 20,
        "specialties": [
          "Gynecologic Oncology",
          "Vulvar Reconstructive Surgery",
          "Radical Excision"
        ]
      },
      {
        "name": "Dr. Amita Maheshwari",
        "title": "Professor & Head, Gyne-Oncology",
        "institution": "Tata Memorial Hospital",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Gynecologic Oncology",
          "Vulvar Cancer",
          "Women's Cancer Center"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Vulvar Cancer and how is it diagnosed?",
        "answer": "Cancer of the external female genitalia, most often squamous cell carcinoma. It arises by two distinct routes: an HPV-driven pathway affecting younger women, and a pathway from chronic lichen sclerosus and differentiated VIN in older women, which carries a worse prognosis. Diagnosis is frequently delayed because symptoms are attributed to thrush or menopausal change. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Vulvar Cancer?",
        "answer": "Standard first-line protocols include: Wide radical local excision with an adequate margin, Sentinel lymph node biopsy for tumours under 4 cm with a clinically negative groin, Inguinofemoral lymphadenectomy where sentinel biopsy is not appropriate. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Vulvar Cancer?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: HPV 16 with p16 positivity in the younger cohort, TP53 in the lichen sclerosus-associated pathway. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Vulval itching lasting beyond a few weeks in a postmenopausal woman warrants examination and biopsy, not repeat antifungals. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Sentinel node techniques to reduce the substantial lymphoedema burden of full lymphadenectomy, immunotherapy in advanced HPV-driven disease, and HPV vaccination as primary prevention are the key developments. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Vulvar Cancer through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Vulvar Cancer Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Vulvar Cancer. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/vulvar-cancer",
      "keywords": [
        "Vulvar Cancer treatment",
        "Vulvar Cancer specialists",
        "Vulvar Cancer clinical trials",
        "Vulvar Cancer second opinion",
        "Vulvar Cancer prognosis",
        "Vulvar Cancer NCI cancer centers",
        "rare cancer care Vulvar Cancer"
      ]
    }
  },
  {
    "slug": "vaginal-cancer",
    "aliases": [
      "vaginal-cancer"
    ],
    "path": "/rare-cancers/vaginal-cancer",
    "name": "Vaginal Cancer",
    "shortName": "Vaginal Cancer",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Gynecologic",
    "description": "A rare gynaecological cancer arising primarily in the vagina rather than spreading there from the cervix or vulva - a distinction that must be made before the diagnosis is accepted. Most are HPV-related squamous cell carcinomas. Clear cell adenocarcinoma of the vagina has a specific historical association with in-utero diethylstilbestrol exposure.",
    "symptoms": [
      "Abnormal vaginal bleeding, including after intercourse or after menopause",
      "Watery, blood-stained or offensive discharge",
      "A mass or lump felt in the vagina",
      "Pain during intercourse or in the pelvis",
      "Urinary symptoms or constipation with advanced disease"
    ],
    "treatmentOptions": [
      "Definitive chemoradiotherapy with brachytherapy - the standard for most stages",
      "Radical surgery for selected early upper-vaginal tumours",
      "Brachytherapy alone for very early superficial disease",
      "Cisplatin-based concurrent chemotherapy",
      "Vaginal dilator therapy and survivorship support for stenosis and sexual function"
    ],
    "prognosis": "Stage-dependent. Five-year survival is roughly 75-85% for stage I but falls substantially with pelvic sidewall involvement or nodal spread. Overall five-year survival across all stages is around 50%.",
    "researchStatus": "Image-guided adaptive brachytherapy has improved local control meaningfully; immunotherapy in recurrent HPV-associated disease is under study, largely extrapolated from cervical cancer trials given the rarity.",
    "molecularMarkers": [
      "HPV 16/18 with p16 positivity",
      "PIK3CA",
      "TP53"
    ],
    "whoItAffects": "Women over 60 for squamous disease; young women for DES-related clear cell adenocarcinoma",
    "incidence": "Approximately 0.6-1 per 100,000 women per year",
    "redFlags": [
      "Exclude cervical and vulval primaries before diagnosing primary vaginal cancer"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Sarah M. Temkin",
        "title": "Gynecologic Oncologist",
        "institution": "Case Western/Seidman Cancer Center",
        "city": "Cleveland",
        "region": "USA",
        "country": "USA",
        "experienceYears": 20,
        "specialties": [
          "Gynecologic Oncology",
          "Vaginal Cancer",
          "Brachytherapy"
        ]
      },
      {
        "name": "Dr. Lim Lay Hoon",
        "title": "Radiation Oncologist",
        "institution": "NCIS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Radiation Oncology",
          "Gynecologic Brachytherapy",
          "Interstitial Implants"
        ]
      },
      {
        "name": "Prof. Ignace Vergote",
        "title": "Gynecologic Oncologist",
        "institution": "University Hospital Leuven",
        "city": "Leuven",
        "region": "Europe",
        "country": "Belgium",
        "experienceYears": 35,
        "specialties": [
          "Gynecologic Oncology",
          "Rare Vaginal Cancers",
          "European Guidelines"
        ]
      },
      {
        "name": "Dr. Swapna Misra",
        "title": "Gynecologic Oncologist",
        "institution": "KEM Hospital",
        "city": "Mumbai",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Gynecologic Oncology",
          "Vaginal Cancer",
          "Multidisciplinary Care"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Vaginal Cancer and how is it diagnosed?",
        "answer": "A rare gynaecological cancer arising primarily in the vagina rather than spreading there from the cervix or vulva - a distinction that must be made before the diagnosis is accepted. Most are HPV-related squamous cell carcinomas. Clear cell adenocarcinoma of the vagina has a specific historical association with in-utero diethylstilbestrol exposure. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Vaginal Cancer?",
        "answer": "Standard first-line protocols include: Definitive chemoradiotherapy with brachytherapy - the standard for most stages, Radical surgery for selected early upper-vaginal tumours, Brachytherapy alone for very early superficial disease. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Vaginal Cancer?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: HPV 16/18 with p16 positivity, PIK3CA, TP53. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Exclude cervical and vulval primaries before diagnosing primary vaginal cancer. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Image-guided adaptive brachytherapy has improved local control meaningfully; immunotherapy in recurrent HPV-associated disease is under study, largely extrapolated from cervical cancer trials given the rarity. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Vaginal Cancer through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Vaginal Cancer Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Vaginal Cancer. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/vaginal-cancer",
      "keywords": [
        "Vaginal Cancer treatment",
        "Vaginal Cancer specialists",
        "Vaginal Cancer clinical trials",
        "Vaginal Cancer second opinion",
        "Vaginal Cancer prognosis",
        "Vaginal Cancer NCI cancer centers",
        "rare cancer care Vaginal Cancer"
      ]
    }
  },
  {
    "slug": "osteosarcoma",
    "aliases": [
      "osteosarcoma",
      "bone-sarcoma"
    ],
    "path": "/rare-cancers/osteosarcoma",
    "name": "Osteosarcoma",
    "shortName": "Osteosarcoma",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Bone Cancer",
    "description": "The commonest primary bone cancer, arising from bone-forming cells. It typically develops in the growing ends of long bones - around the knee and in the upper arm - during the adolescent growth spurt. A second incidence peak occurs in older adults, often on a background of Paget disease or prior radiotherapy.",
    "symptoms": [
      "Deep bone pain, characteristically worse at night and not relieved by rest",
      "Swelling or a lump over the affected bone",
      "Reduced movement in the nearby joint, or a limp",
      "A fracture through the weakened bone after minor injury",
      "Pain often mistaken for a sports injury or growing pains for months"
    ],
    "treatmentOptions": [
      "Neoadjuvant MAP chemotherapy - high-dose methotrexate, doxorubicin and cisplatin",
      "Limb-salvage surgery with endoprosthetic reconstruction wherever feasible",
      "Amputation or rotationplasty where limb salvage is not possible",
      "Adjuvant chemotherapy guided by the degree of tumour necrosis in the resected specimen",
      "Metastasectomy for resectable lung metastases, which can still be curative"
    ],
    "prognosis": "Five-year survival is approximately 60-70% for localised disease treated with combined chemotherapy and surgery, but roughly 20-30% with metastases at presentation. Good histological response to neoadjuvant chemotherapy is the strongest favourable prognostic factor. Outcomes have plateaued since the 1980s, which is the central problem in the field.",
    "researchStatus": "Trials of mifamurtide, immunotherapy, and agents targeting the genomically chaotic osteosarcoma genome continue; international collaboration through groups such as EURAMOS is essential given plateaued outcomes.",
    "molecularMarkers": [
      "TP53 including germline (Li-Fraumeni)",
      "RB1 including hereditary retinoblastoma survivors",
      "Chromothripsis and high genomic instability",
      "MDM2/CDK4 in low-grade variants"
    ],
    "whoItAffects": "Adolescents and young adults 10-20 years; second peak over 60; male predominance",
    "incidence": "Approximately 3-4 per million per year, higher during adolescence",
    "redFlags": [
      "Night bone pain in a teenager - X-ray, do not just prescribe analgesia",
      "Biopsy only at the treating sarcoma centre - a poorly placed tract can cost the limb"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "RED_FLAGS_ONLY",
    "specialists": [
      {
        "name": "Dr. Richard Gorlick",
        "title": "Division Head of Pediatrics, Director of Osteosarcoma Research Laboratory",
        "institution": "MD Anderson Cancer Center",
        "city": "Houston",
        "region": "USA",
        "country": "USA",
        "experienceYears": 25,
        "specialties": [
          "Pediatric Oncology",
          "Osteosarcoma",
          "Osteosarcoma Research"
        ]
      },
      {
        "name": "Dr. Paul A. Meyers",
        "title": "Pediatric Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 40,
        "specialties": [
          "Pediatric Oncology",
          "Osteosarcoma",
          "Chemotherapy Protocols"
        ]
      },
      {
        "name": "Dr. Kam Chan Hui",
        "title": "Pediatric Oncologist",
        "institution": "KKH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Pediatric Oncology",
          "Osteosarcoma",
          "MAP Chemotherapy"
        ]
      },
      {
        "name": "Prof. Stefan Bielack",
        "title": "Pediatric Oncologist",
        "institution": "Olgahospital",
        "city": "Stuttgart",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 30,
        "specialties": [
          "Pediatric Oncology",
          "Osteosarcoma",
          "COSS Trials"
        ]
      },
      {
        "name": "Dr. G. K. Prabhu",
        "title": "Orthopedic Oncologist",
        "institution": "HCG",
        "city": "Bengaluru",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Orthopedic Oncology",
          "Limb-sparing Surgery",
          "Osteosarcoma"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Osteosarcoma and how is it diagnosed?",
        "answer": "The commonest primary bone cancer, arising from bone-forming cells. It typically develops in the growing ends of long bones - around the knee and in the upper arm - during the adolescent growth spurt. A second incidence peak occurs in older adults, often on a background of Paget disease or prior radiotherapy. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Osteosarcoma?",
        "answer": "Standard first-line protocols include: Neoadjuvant MAP chemotherapy - high-dose methotrexate, doxorubicin and cisplatin, Limb-salvage surgery with endoprosthetic reconstruction wherever feasible, Amputation or rotationplasty where limb salvage is not possible. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Osteosarcoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: TP53 including germline (Li-Fraumeni), RB1 including hereditary retinoblastoma survivors, Chromothripsis and high genomic instability, MDM2/CDK4 in low-grade variants. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Night bone pain in a teenager - X-ray, do not just prescribe analgesia; Biopsy only at the treating sarcoma centre - a poorly placed tract can cost the limb. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Trials of mifamurtide, immunotherapy, and agents targeting the genomically chaotic osteosarcoma genome continue; international collaboration through groups such as EURAMOS is essential given plateaued outcomes. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Osteosarcoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Osteosarcoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Osteosarcoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/osteosarcoma",
      "keywords": [
        "Osteosarcoma treatment",
        "Osteosarcoma specialists",
        "Osteosarcoma clinical trials",
        "Osteosarcoma second opinion",
        "Osteosarcoma prognosis",
        "Osteosarcoma NCI cancer centers",
        "rare cancer care Osteosarcoma"
      ]
    }
  },
  {
    "slug": "ewing-sarcoma",
    "aliases": [
      "ewing-sarcoma",
      "ewings-sarcoma"
    ],
    "path": "/rare-cancers/ewing-sarcoma",
    "name": "Ewing Sarcoma",
    "shortName": "Ewing Sarcoma",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Bone/Soft Tissue",
    "description": "An aggressive cancer of bone and soft tissue driven by a fusion between EWSR1 and an ETS-family transcription factor, most often FLI1. It affects children and young adults, commonly involving the pelvis, chest wall and long bones, and requires both intensive systemic chemotherapy and definitive local control.",
    "symptoms": [
      "Bone pain that worsens over weeks and is often worse at night",
      "Swelling, warmth or a tender lump over the affected area",
      "Fever, weight loss and fatigue, which can mimic infection",
      "A limp or reduced use of a limb",
      "Back pain with neurological signs in spinal or pelvic tumours"
    ],
    "treatmentOptions": [
      "Interval-compressed VDC/IE chemotherapy - vincristine, doxorubicin, cyclophosphamide alternating with ifosfamide and etoposide",
      "Surgical resection for local control where achievable",
      "Definitive radiotherapy where surgery would cause unacceptable morbidity",
      "High-dose chemotherapy with stem cell rescue in selected high-risk patients",
      "Clinical trial enrolment, particularly at relapse"
    ],
    "prognosis": "Five-year survival is roughly 70-80% for localised disease treated on modern interval-compressed protocols, but 20-30% with metastases at diagnosis. Isolated lung metastases carry a better outlook than bone or marrow involvement. Relapse after two years is uncommon but does occur.",
    "researchStatus": "Targeting the EWSR1-FLI1 fusion directly, PARP inhibitor combinations, and immunotherapy including CAR-T against surface targets are the leading research directions.",
    "molecularMarkers": [
      "EWSR1-FLI1 fusion, t(11;22)(q24;q12)",
      "EWSR1-ERG - variant",
      "CD99 membranous positivity",
      "STAG2 and TP53 as adverse factors"
    ],
    "whoItAffects": "Children, adolescents and young adults, peak 10-20 years; markedly commoner in people of European ancestry",
    "incidence": "Approximately 3 per million per year in the affected age group",
    "redFlags": [
      "Persistent bone pain with fever in a young person can be mistaken for osteomyelitis - image before treating as infection"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Leonard Wexler",
        "title": "Pediatric Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "USA",
        "country": "USA",
        "experienceYears": 35,
        "specialties": [
          "Pediatric Oncology",
          "Ewing Sarcoma",
          "INT-0091 Trials"
        ]
      },
      {
        "name": "Dr. Viulnerability Liu",
        "title": "Pediatric Oncologist",
        "institution": "NUH",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 10,
        "specialties": [
          "Pediatric Oncology",
          "Ewing Sarcoma",
          "VIDE/VAI Chemotherapy"
        ]
      },
      {
        "name": "Prof. Theodoros Gr\u00fcnewald",
        "title": "Oncologist",
        "institution": "University Hospital Essen",
        "city": "Essen",
        "region": "Europe",
        "country": "Germany",
        "experienceYears": 15,
        "specialties": [
          "Oncology",
          "Ewing Sarcoma Biology",
          "Euro-E.W.I.N.G."
        ]
      },
      {
        "name": "Dr. Santanu Sen",
        "title": "Pediatric Oncologist",
        "institution": "Private Practice",
        "city": "Kolkata",
        "region": "India",
        "country": "India",
        "experienceYears": 20,
        "specialties": [
          "Pediatric Oncology",
          "Ewing Sarcoma",
          "Cooperative Protocols"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Ewing Sarcoma and how is it diagnosed?",
        "answer": "An aggressive cancer of bone and soft tissue driven by a fusion between EWSR1 and an ETS-family transcription factor, most often FLI1. It affects children and young adults, commonly involving the pelvis, chest wall and long bones, and requires both intensive systemic chemotherapy and definitive local control. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Ewing Sarcoma?",
        "answer": "Standard first-line protocols include: Interval-compressed VDC/IE chemotherapy - vincristine, doxorubicin, cyclophosphamide alternating with ifosfamide and etoposide, Surgical resection for local control where achievable, Definitive radiotherapy where surgery would cause unacceptable morbidity. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Ewing Sarcoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: EWSR1-FLI1 fusion, t(11;22)(q24;q12), EWSR1-ERG - variant, CD99 membranous positivity, STAG2 and TP53 as adverse factors. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Persistent bone pain with fever in a young person can be mistaken for osteomyelitis - image before treating as infection. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Targeting the EWSR1-FLI1 fusion directly, PARP inhibitor combinations, and immunotherapy including CAR-T against surface targets are the leading research directions. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Ewing Sarcoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Ewing Sarcoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Ewing Sarcoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/ewing-sarcoma",
      "keywords": [
        "Ewing Sarcoma treatment",
        "Ewing Sarcoma specialists",
        "Ewing Sarcoma clinical trials",
        "Ewing Sarcoma second opinion",
        "Ewing Sarcoma prognosis",
        "Ewing Sarcoma NCI cancer centers",
        "rare cancer care Ewing Sarcoma"
      ]
    }
  },
  {
    "slug": "retinoblastoma",
    "aliases": [
      "retinoblastoma"
    ],
    "path": "/rare-cancers/retinoblastoma",
    "name": "Retinoblastoma",
    "shortName": "Retinoblastoma",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Eye Cancer",
    "description": "The commonest eye cancer of childhood, caused by loss of both copies of the RB1 tumour suppressor gene. Roughly 40% of cases are heritable, usually bilateral and diagnosed earlier, and carry a lifelong increased risk of second cancers. It is one of the most curable childhood cancers when diagnosed early - and one of the most lethal when diagnosis is late, which is why survival differs so sharply between countries.",
    "symptoms": [
      "A white reflection in the pupil, often first noticed in a photograph (leukocoria)",
      "A newly turned or squinting eye (strabismus)",
      "A red, painful or swollen eye",
      "Poor vision or a change in iris colour",
      "A visibly enlarging eye in advanced disease"
    ],
    "treatmentOptions": [
      "Systemic chemotherapy to shrink tumours (chemoreduction) followed by focal therapy",
      "Intra-arterial chemotherapy delivered via the ophthalmic artery",
      "Intravitreal chemotherapy for vitreous seeding",
      "Focal consolidation with laser photocoagulation or cryotherapy",
      "Plaque brachytherapy or, less often, external beam radiotherapy",
      "Enucleation for advanced unilateral disease - still curative and sometimes the right choice",
      "Germline RB1 testing for the child and screening of siblings"
    ],
    "prognosis": "Survival exceeds 95% in high-income countries where disease is caught while intraocular. It remains substantially lower in settings where children present with extraocular extension - which is a health-system problem rather than a biological one. Eye and vision preservation depend on stage at presentation.",
    "researchStatus": "Refinement of intra-arterial and intravitreal delivery to preserve vision, and global initiatives to reduce diagnostic delay in low- and middle-income settings, are the two most consequential areas of work.",
    "molecularMarkers": [
      "RB1 biallelic inactivation",
      "Germline RB1 in about 40%",
      "MYCN amplification in a rare RB1-wild-type subset"
    ],
    "whoItAffects": "Infants and young children, usually diagnosed under 5; bilateral disease presents earlier",
    "incidence": "Approximately 1 in 15,000-20,000 live births",
    "redFlags": [
      "White pupillary reflex at any age - same-week ophthalmology referral",
      "Heritable disease requires lifelong surveillance for second malignancies"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "RED_FLAGS_ONLY",
    "specialists": [
      {
        "name": "Dr. Jesse L. Berry",
        "title": "Ocular Oncologist",
        "institution": "Children's Hospital LA",
        "city": "Los Angeles",
        "region": "USA",
        "country": "USA",
        "experienceYears": 10,
        "specialties": [
          "Ocular Oncology",
          "Retinoblastoma",
          "Intra-arterial Chemotherapy"
        ]
      },
      {
        "name": "Dr. Audrey Chia",
        "title": "Senior Pediatric Ophthalmologist",
        "institution": "SNEC",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Pediatric Ophthalmology",
          "Retinoblastoma",
          "Chemoreduction"
        ]
      },
      {
        "name": "Prof. David H. Abramson",
        "title": "Ocular Oncologist",
        "institution": "MSKCC",
        "city": "New York",
        "region": "Europe",
        "country": "USA",
        "experienceYears": 40,
        "specialties": [
          "Ocular Oncology",
          "Retinoblastoma",
          "Local Therapies"
        ]
      },
      {
        "name": "Dr. Bikramjit Pal",
        "title": "Ocular Oncologist",
        "institution": "Sankara Nethralaya",
        "city": "Chennai",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Ocular Oncology",
          "Retinoblastoma",
          "Globe-conserving Treatment"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Retinoblastoma and how is it diagnosed?",
        "answer": "The commonest eye cancer of childhood, caused by loss of both copies of the RB1 tumour suppressor gene. Roughly 40% of cases are heritable, usually bilateral and diagnosed earlier, and carry a lifelong increased risk of second cancers. It is one of the most curable childhood cancers when diagnosed early - and one of the most lethal when diagnosis is late, which is why survival differs so sharply between countries. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Retinoblastoma?",
        "answer": "Standard first-line protocols include: Systemic chemotherapy to shrink tumours (chemoreduction) followed by focal therapy, Intra-arterial chemotherapy delivered via the ophthalmic artery, Intravitreal chemotherapy for vitreous seeding. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Retinoblastoma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: RB1 biallelic inactivation, Germline RB1 in about 40%, MYCN amplification in a rare RB1-wild-type subset. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: White pupillary reflex at any age - same-week ophthalmology referral; Heritable disease requires lifelong surveillance for second malignancies. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Refinement of intra-arterial and intravitreal delivery to preserve vision, and global initiatives to reduce diagnostic delay in low- and middle-income settings, are the two most consequential areas of work. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Retinoblastoma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Retinoblastoma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Retinoblastoma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/retinoblastoma",
      "keywords": [
        "Retinoblastoma treatment",
        "Retinoblastoma specialists",
        "Retinoblastoma clinical trials",
        "Retinoblastoma second opinion",
        "Retinoblastoma prognosis",
        "Retinoblastoma NCI cancer centers",
        "rare cancer care Retinoblastoma"
      ]
    }
  },
  {
    "slug": "malignant-mesothelioma",
    "aliases": [
      "malignant-mesothelioma",
      "mesothelioma",
      "pleural-mesothelioma"
    ],
    "path": "/rare-cancers/malignant-mesothelioma",
    "name": "Malignant Mesothelioma",
    "shortName": "Malignant Mesothelioma",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Pleural Tumor",
    "description": "A cancer of the mesothelial lining of the chest (pleura), abdomen (peritoneum) or, rarely, the heart or testis. It is overwhelmingly caused by asbestos exposure, typically 20-50 years earlier, which makes occupational history central to diagnosis and to any compensation claim.",
    "symptoms": [
      "Breathlessness from fluid around the lung",
      "Chest wall pain that is persistent and often described as dull and boring",
      "Persistent dry cough",
      "Unintended weight loss, sweats and fatigue",
      "Abdominal swelling and pain in peritoneal disease"
    ],
    "treatmentOptions": [
      "Nivolumab plus ipilimumab as first-line therapy, particularly for non-epithelioid histology",
      "Pemetrexed-platinum chemotherapy, with or without bevacizumab",
      "Pleurectomy/decortication in selected fit patients as part of multimodal therapy",
      "Cytoreductive surgery with HIPEC for peritoneal mesothelioma, which has a far better outlook",
      "Pleurodesis or indwelling pleural catheter for effusion control",
      "Early specialist palliative care and support with occupational compensation claims"
    ],
    "prognosis": "Poor for pleural disease, with median survival historically around 12-18 months, extended by immunotherapy in the non-epithelioid group. Epithelioid histology does better than sarcomatoid. Peritoneal mesothelioma treated with complete cytoreduction and HIPEC is a genuine outlier, with median survival measured in years.",
    "researchStatus": "Immunotherapy combinations and maintenance strategies, BAP1-directed synthetic lethality including EZH2 and PARP inhibition, and CAR-T against mesothelin are the principal research directions.",
    "molecularMarkers": [
      "BAP1 loss including germline (BAP1 tumour predisposition syndrome)",
      "CDKN2A homozygous deletion",
      "NF2",
      "Calretinin and WT1 positivity by IHC"
    ],
    "whoItAffects": "Adults over 60 with prior asbestos exposure; strong male predominance reflecting occupational patterns",
    "incidence": "Approximately 1-2 per 100,000 per year in industrialised countries; still rising where asbestos use ended late",
    "redFlags": [
      "Take an occupational history in every unexplained pleural effusion",
      "Germline BAP1 testing where there is a suggestive family history"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Hedy Lee Kindler",
        "title": "Medical Oncologist",
        "institution": "University of Chicago",
        "city": "Chicago",
        "region": "USA",
        "country": "USA",
        "experienceYears": 25,
        "specialties": [
          "Medical Oncology",
          "Mesothelioma",
          "Pemetrexed Trials"
        ]
      },
      {
        "name": "Dr. Vyas Prasad",
        "title": "Thoracic Surgeon",
        "institution": "NCID/NCCS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 20,
        "specialties": [
          "Thoracic Surgery",
          "Mesothelioma",
          "Extrapleural Pneumonectomy"
        ]
      },
      {
        "name": "Prof. Marc de Perrot",
        "title": "Thoracic Surgeon",
        "institution": "Toronto General Hospital",
        "city": "Toronto",
        "region": "Europe",
        "country": "Canada",
        "experienceYears": 20,
        "specialties": [
          "Thoracic Surgery",
          "Mesothelioma Surgery",
          "Immunotherapy"
        ]
      },
      {
        "name": "Dr. S. Vijayakumar",
        "title": "Medical Oncologist",
        "institution": "Apollo Specialty",
        "city": "Chennai",
        "region": "India",
        "country": "India",
        "experienceYears": 15,
        "specialties": [
          "Medical Oncology",
          "Mesothelioma",
          "Multi-modality Treatment"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Malignant Mesothelioma and how is it diagnosed?",
        "answer": "A cancer of the mesothelial lining of the chest (pleura), abdomen (peritoneum) or, rarely, the heart or testis. It is overwhelmingly caused by asbestos exposure, typically 20-50 years earlier, which makes occupational history central to diagnosis and to any compensation claim. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Malignant Mesothelioma?",
        "answer": "Standard first-line protocols include: Nivolumab plus ipilimumab as first-line therapy, particularly for non-epithelioid histology, Pemetrexed-platinum chemotherapy, with or without bevacizumab, Pleurectomy/decortication in selected fit patients as part of multimodal therapy. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Malignant Mesothelioma?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: BAP1 loss including germline (BAP1 tumour predisposition syndrome), CDKN2A homozygous deletion, NF2, Calretinin and WT1 positivity by IHC. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Take an occupational history in every unexplained pleural effusion; Germline BAP1 testing where there is a suggestive family history. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Immunotherapy combinations and maintenance strategies, BAP1-directed synthetic lethality including EZH2 and PARP inhibition, and CAR-T against mesothelin are the principal research directions. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Malignant Mesothelioma through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Malignant Mesothelioma Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Malignant Mesothelioma. Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/malignant-mesothelioma",
      "keywords": [
        "Malignant Mesothelioma treatment",
        "Malignant Mesothelioma specialists",
        "Malignant Mesothelioma clinical trials",
        "Malignant Mesothelioma second opinion",
        "Malignant Mesothelioma prognosis",
        "Malignant Mesothelioma NCI cancer centers",
        "rare cancer care Malignant Mesothelioma"
      ]
    }
  },
  {
    "slug": "cancer-of-unknown-primary-cup",
    "aliases": [
      "cancer-of-unknown-primary",
      "cup",
      "unknown-primary-cancer"
    ],
    "path": "/rare-cancers/cancer-of-unknown-primary-cup",
    "name": "Cancer of Unknown Primary (CUP)",
    "shortName": "Cancer of Unknown Primary",
    "category": "rare",
    "categoryLabel": "Rare Malignancy (< 6 cases per 100,000 / year)",
    "type": "Unknown Primary",
    "description": "A metastatic cancer for which no primary site can be identified after a standard diagnostic work-up. It is a working diagnosis, not a single disease. The essential clinical task is to identify the favourable subsets - which are treated as if the primary were known, often with good outcomes - and to distinguish them from the unfavourable majority.",
    "symptoms": [
      "Enlarged lymph nodes without an obvious source",
      "Unexplained weight loss and fatigue",
      "Pain at the site of metastases, commonly bone or liver",
      "Abdominal swelling from ascites",
      "Breathlessness from lung or pleural involvement"
    ],
    "treatmentOptions": [
      "A structured immunohistochemistry algorithm to assign likely tissue of origin",
      "Comprehensive genomic profiling and, where available, tissue-of-origin molecular classifiers",
      "Site-specific treatment for favourable subsets - for example, treating isolated axillary nodal adenocarcinoma in a woman as breast cancer",
      "Platinum-based combination chemotherapy for unfavourable subsets",
      "Immunotherapy where MSI-H, high tumour mutational burden or high PD-L1 is present",
      "Targeted therapy against any actionable alteration found, regardless of presumed origin"
    ],
    "prognosis": "Highly variable and driven entirely by subset. Favourable subsets - including isolated axillary nodal adenocarcinoma in women, squamous carcinoma in cervical nodes, peritoneal serous carcinoma, and single resectable metastases - can achieve outcomes comparable to the equivalent known primary. The unfavourable majority has median survival of approximately 6-12 months.",
    "researchStatus": "Molecular tissue-of-origin classifiers and site-specific therapy directed by them have been tested in randomised trials with mixed results; the clearer benefit so far has come from acting on actionable alterations and immunotherapy biomarkers rather than from predicted site alone.",
    "molecularMarkers": [
      "IHC panel - CK7/CK20, TTF-1, GATA3, CDX2, PAX8, p40",
      "MSI-H / dMMR",
      "Tumour mutational burden",
      "Actionable fusions - NTRK, ALK, RET"
    ],
    "whoItAffects": "Adults, median 60-70; incidence falling as imaging and pathology improve",
    "incidence": "Approximately 2-5% of all cancer diagnoses, and declining",
    "redFlags": [
      "Always search actively for a favourable subset before defaulting to empirical chemotherapy"
    ],
    "lastReviewed": "2026-08-13",
    "reviewScope": "UNREVIEWED",
    "specialists": [
      {
        "name": "Dr. Anthony Greco",
        "title": "Medical Oncologist",
        "institution": "Sarah Cannon Research Institute",
        "city": "Nashville",
        "region": "USA",
        "country": "USA",
        "experienceYears": 45,
        "specialties": [
          "Medical Oncology",
          "CUP",
          "Empiric Chemotherapy"
        ]
      },
      {
        "name": "Dr. Ravindran Kanesvaran",
        "title": "Medical Oncologist",
        "institution": "NCCS",
        "city": "Singapore",
        "region": "Singapore",
        "country": "Singapore",
        "experienceYears": 15,
        "specialties": [
          "Medical Oncology",
          "CUP Service",
          "Molecular Profiling"
        ]
      },
      {
        "name": "Prof. Fizazi Karim",
        "title": "Medical Oncologist",
        "institution": "Gustave Roussy",
        "city": "Villejuif",
        "region": "Europe",
        "country": "France",
        "experienceYears": 20,
        "specialties": [
          "Medical Oncology",
          "CUP Trials",
          "ESMO Guidelines"
        ]
      },
      {
        "name": "Dr. Harit Chaturvedi",
        "title": "Surgical Oncologist",
        "institution": "Max Healthcare",
        "city": "Delhi",
        "region": "India",
        "country": "India",
        "experienceYears": 30,
        "specialties": [
          "Surgical Oncology",
          "CUP",
          "Treatable Subsets"
        ]
      }
    ],
    "recommendedCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center",
        "slug": "memorial-sloan-kettering-cancer-center",
        "city": "New York",
        "state": "NY",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,000+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Robotic Surgery",
          "Phase I Unit"
        ],
        "relevance": "World-leading rare tumor board & pediatric solid tumor protocols"
      },
      {
        "name": "University of Texas MD Anderson Cancer Center",
        "slug": "university-of-texas-md-anderson-cancer-center",
        "city": "Houston",
        "state": "TX",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,200+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "Largest specialized rare cancer and sarcoma multidisciplinary program"
      },
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "slug": "dana-farber-harvard-cancer-center",
        "city": "Boston",
        "state": "MA",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "1,100+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "Phase I Unit"
        ],
        "relevance": "Pioneering genomic molecular tumor boards & rare histologies"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "slug": "mayo-clinic-comprehensive-cancer-center-minnesota",
        "city": "Rochester",
        "state": "MN",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "trials": "600+ Active Clinical Trials",
        "technologies": [
          "Proton Therapy",
          "CAR-T Cell Therapy",
          "CyberKnife SBRT",
          "Phase I Unit"
        ],
        "relevance": "High-volume surgical oncology & rare endocrine/neuroendocrine expertise"
      }
    ],
    "faqs": [
      {
        "question": "What is Cancer of Unknown Primary (CUP) and how is it diagnosed?",
        "answer": "A metastatic cancer for which no primary site can be identified after a standard diagnostic work-up. It is a working diagnosis, not a single disease. The essential clinical task is to identify the favourable subsets - which are treated as if the primary were known, often with good outcomes - and to distinguish them from the unfavourable majority. Definitive diagnosis requires expert histopathologic review by a specialized subspecialty pathologist, typically supported by immunohistochemistry (IHC) and next-generation sequencing (NGS)."
      },
      {
        "question": "What are the standard treatment options for Cancer of Unknown Primary (CUP)?",
        "answer": "Standard first-line protocols include: A structured immunohistochemistry algorithm to assign likely tissue of origin, Comprehensive genomic profiling and, where available, tissue-of-origin molecular classifiers, Site-specific treatment for favourable subsets - for example, treating isolated axillary nodal adenocarcinoma in a woman as breast cancer. Because rare cancers carry unique biological behavior, treatment decisions should be guided by a dedicated multidisciplinary tumor board."
      },
      {
        "question": "What genomic alterations and molecular markers are tested in Cancer of Unknown Primary (CUP)?",
        "answer": "Diagnostic and therapeutic molecular testing routinely evaluates: IHC panel - CK7/CK20, TTF-1, GATA3, CDX2, PAX8, p40, MSI-H / dMMR, Tumour mutational burden, Actionable fusions - NTRK, ALK, RET. Identifying these actionable lesions enables targeted therapy selection and precision clinical trial enrollment."
      },
      {
        "question": "What are the critical red-flag symptoms requiring emergency evaluation?",
        "answer": "Patients and caregivers should seek urgent emergency oncologic evaluation if any of the following occur: Always search actively for a favourable subset before defaulting to empirical chemotherapy. Immediate referral to a tertiary specialist is vital."
      },
      {
        "question": "What active clinical trials and emerging therapies are being investigated?",
        "answer": "Molecular tissue-of-origin classifiers and site-specific therapy directed by them have been tested in randomised trials with mixed results; the clearer benefit so far has come from acting on actionable alterations and immunotherapy biomarkers rather than from predicted site alone. Ask your oncologist about matching to Phase I/II basket trials or international rare disease consortia."
      },
      {
        "question": "How can I obtain an online second opinion for Cancer of Unknown Primary (CUP) through ByOnco?",
        "answer": "ByOnco connects patients diagnosed with rare cancers directly to NCI-fellowship trained subspecialists and academic tumor boards. You can upload digital DICOM scans and pathology slides to receive an independent treatment review and guideline verification within 12 to 72 hours."
      }
    ],
    "meta": {
      "title": "Cancer of Unknown Primary (CUP) Care Guide: Specialists, Clinical Trials & Treatment | ByOnco",
      "description": "Comprehensive clinical guide to Cancer of Unknown Primary (CUP). Discover top NCI-designated treatment centers, vetted specialist oncologists, cutting-edge clinical trials, molecular biomarkers, and second opinions.",
      "canonical": "https://www.getbyonco.com/rare-cancers/cancer-of-unknown-primary-cup",
      "keywords": [
        "Cancer of Unknown Primary (CUP) treatment",
        "Cancer of Unknown Primary (CUP) specialists",
        "Cancer of Unknown Primary (CUP) clinical trials",
        "Cancer of Unknown Primary (CUP) second opinion",
        "Cancer of Unknown Primary (CUP) prognosis",
        "Cancer of Unknown Primary (CUP) NCI cancer centers",
        "rare cancer care Cancer of Unknown Primary"
      ]
    }
  }
];

export const RARE_CANCERS_BY_SLUG = RARE_CANCERS_DATA.reduce((acc, item) => {
  acc[item.slug] = item;
  if (item.aliases && item.aliases.length > 0) {
    item.aliases.forEach((alias) => {
      acc[alias] = item;
    });
  }
  return acc;
}, {});

export const RARE_CANCERS_BY_CATEGORY = {
  'ultra-rare': RARE_CANCERS_DATA.filter((c) => c.category === 'ultra-rare'),
  'very-rare': RARE_CANCERS_DATA.filter((c) => c.category === 'very-rare'),
  'rare': RARE_CANCERS_DATA.filter((c) => c.category === 'rare'),
};

export const TOTAL_RARE_CANCERS_COUNT = 39;
